import React, { useState, useEffect, useCallback, useRef } from 'react';
import { appVersion, isIOS } from '../services/appVersion.mjs';
import { secureAsyncStore, isEvictable, secureKeyNames } from '../services/secureStore.mjs';
import { ChatSessions } from '../services/chatSessions.mjs';
import { encryptMessage, DECRYPT_ERROR_TEXT } from '../services/chatCrypto.mjs';
import { ChatIntake, legacyVerdict } from '../services/chatIntake.mjs';
import { JobQueue, holdError } from '../services/jobQueue.mjs';
import { expectAck, ack as ackSend, ackKey, ACK_TIMEOUT_MS } from '../services/sendAck.mjs';
import { keepUnacknowledged } from '../services/chatMerge.mjs';
import { forgetAccountKey } from '../services/deviceListCache.mjs';
import { sipStatusReports } from '../services/sipStatus.mjs';
import { identity as deviceIdentity, tofu } from '../services/tunnel';
import { makeBundleVerifier, trustDevice, makeIncomingVerifier, deviceIdOf } from '../services/chatTrust.mjs';
import { loadNotices, recordNotice, clearNotice } from '../services/trustNotices.mjs';
import { utc, fmtDate, fmtTime, fmtDur, formatLastSeen } from '../services/time.mjs';
import { serverHostname, publicOrigin, sipWsUrls } from '../services/serverBase.mjs';
import { displayName } from '../services/displayName';
import { api } from '../services/api';
import { messageStore, conversationId, lastId, firstId, withTimeout, CACHE_TIMEOUT_MS } from '../services/messageStore.mjs';
import { checkKeyLog, keyLogAlarm as keyLogAlarmStored } from '../services/keyLogWatch.mjs';
import { currentGossip, checkPeerHead } from '../services/keyLogGossip.mjs';
import { makeDeviceListGate, syncPeer, peerMaster } from '../services/deviceListCache.mjs';
import { secureSyncStorage } from '../services/secureStore.mjs';
import { accountState, createAccountList, ACCOUNT_STATE } from '../services/deviceListBootstrap.mjs';
import { sipService } from '../services/sip';
import { wsService } from '../services/ws';
import { useAuth } from '../hooks/useAuth';
import { useNavStack } from '../hooks/useNavStack';
import CallRoom from './CallRoom';
import { ConfirmSheet } from '../components/dashboard/Sheets';
import { FONT, colors, radii } from '../theme';
import {
  Phone, Clock, Users, MessageSquare,
  RefreshCw, ArrowDown,
  Settings
} from 'lucide-react';

// ─── Extracted dashboard modules ─────────────────────────────
import SignalBars from '../components/dashboard/SignalBars';
import CallProtocolPicker from '../components/dashboard/CallProtocolPicker';
import CompactCallBanner from '../components/dashboard/CompactCallBanner';
import ChatOverlay from '../components/dashboard/ChatOverlay';
import { S, activeCallBar } from '../components/dashboard/dashboardStyles';

// ─── Tab content modules ─────────────────────────────────────
import CallsTab from '../components/dashboard/tabs/CallsTab';
import HistoryTab from '../components/dashboard/tabs/HistoryTab';
import ContactsTab from '../components/dashboard/tabs/ContactsTab';
import ChatListTab from '../components/dashboard/tabs/ChatListTab';
import SettingsTab from '../components/dashboard/tabs/SettingsTab';

// ─── CSS keyframes ───────────────────────────────────────────
import '../styles/dashboard-keyframes.css';

const TAB_ICONS = { calls: Phone, history: Clock, users: Users, chat: MessageSquare, more: Settings };
const TAB_LABELS = { calls: 'Звонки', history: 'История', users: 'Контакты', chat: 'Чат', more: 'Настройки' };
const TAB_KEYS = ['calls', 'history', 'users', 'chat', 'more'];

const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || 'dev';

// ─── Link URL helper (fix: GET /links doesn't return url) ───
const getLinkUrl = (link) => link.url || `${publicOrigin()}/join/${link.code}`;

// One store for the whole app: a single IndexedDB connection, shared by every
// conversation.

/**
 * The ratchet, for chat (ADR-016). Created once the account is known, because
 * its keys belong to the account and not to the browser.
 *
 * Held in a ref rather than state: every send and every arriving message needs
 * it, and a re-render must not hand half the app a different one.
 */
function makeSessionStore() {
  // Keychain / EncryptedSharedPreferences on the native clients, localStorage
  // only in a browser where nothing better exists. Sessions evicted mid-
  // conversation is what this replaces.
  const prefix = 'voxiox_ratchet_v1:';
  return {
    get: (k) => secureAsyncStore.get(prefix + k),
    set: (k, v) => secureAsyncStore.set(prefix + k, v),
    del: (k) => secureAsyncStore.del(prefix + k),
  };
}

export default function Dashboard({ addContact: addContactProp }) {
  // Asked at render, never captured at import: the shell injects its
  // bridge at document start, and a module-level constant can run first —
  // which is how the screen said «web» inside the app (03.09.2026).
  const APP_VERSION = appVersion();
  const { user, logout, setUser } = useAuth();
  const [tab, setTab] = useState(addContactProp ? 'users' : 'calls');

  // ─── Core state ──────────────────────────────────────────────
  const [links, setLinks] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [sipConnected, setSipConnected] = useState(false);
  const sipConnectedRef = useRef(false);
  // The two registrations, kept apart (sipStatus.mjs): JsSIP in the WebView
  // and, on iOS, LinPhone natively. `null` native = this phone has none.
  const webSipRef = useRef(false);
  const nativeSipRef = useRef(null);
  const [newLabel, setNewLabel] = useState('');
  const [maxParticipants, setMaxParticipants] = useState(2);
  const [copied, setCopied] = useState('');
  const [tgLink, setTgLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // ─── Room notifications state ──────────────────────────────
  const [roomNotifications, setRoomNotifications] = useState([]);

  // ─── Admin state ─────────────────────────────────────────────
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminError, setAdminError] = useState('');
  const [createdCreds, setCreatedCreds] = useState(null);
  const [credsCopied, setCredsCopied] = useState(false);

  // ─── Contacts (friends) state — ADR-013 ──────────────────────
  const [contactSearch, setContactSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [chatAnchorId, setChatAnchorId] = useState(0);   // where reading stopped
  const [chatHasMore, setChatHasMore] = useState(true);  // more history above?

  // ─── Chat preview state ────────────────────────────────────
  const [lastMessages, setLastMessages] = useState([]);

  // ─── Share contact sheet ────────────────────────────────────
  const [showShareContact, setShowShareContact] = useState(false);

  // ─── Native keep-alive (default ON for calling apps) ───────
  const [keepAlive, setKeepAlive] = useState(true);

  // ─── Chat state ──────────────────────────────────────────────
  const [chatUser, setChatUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});
  const messagesEndRef = useRef(null);
  const chatUserRef = useRef(null);
  // The WS/bridge handlers are registered once, so they read live values through
  // refs instead of capturing the first render's state.
  const usersRef = useRef([]);
  const openChatRef = useRef(null);
  const chatMessagesRef = useRef([]);
  const loadingOlderRef = useRef(false);
  const spamStateRef = useRef({}); // per-user anti-spam state: { [userId]: { ... } }

  // ─── Incoming call overlay ──────────────────────────────────
  const [incomingOverlay, setIncomingOverlay] = useState(null);

  // ─── Create link sheet ──────────────────────────────────────
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [ttlHours, setTtlHours] = useState(24);

  // ─── Dialpad ──────────────────────────────────────────────────
  const [showDialpad, setShowDialpad] = useState(false);

  // ─── Call minimize ─────────────────────────────────────────
  const [callMinimized, setCallMinimized] = useState(false);
  const activeCallRef = useRef(null);

  // ─── Live link statuses ────────────────────────────────────
  const [linkStatuses, setLinkStatuses] = useState({});
  const [qrUrl, setQrUrl] = useState(null);

  // ─── User capabilities & call protocol ────────────────────
  const [userStatuses, setUserStatuses] = useState({});
  const sessionsRef = useRef(null);
  const intakeRef = useRef(null);      // the single intake pipeline (chatIntake.mjs)
  /**
   * One stack for everything that can be backed out of.
   *
   * Until this existed the document history was empty, so Android's
   * webView.canGoBack() was false and the hardware Back button closed the app
   * from inside a conversation. Pushing a real entry when the chat opens is
   * what makes Back mean "close the chat" — no Kotlin involved.
   */
  const nav = useNavStack();
  /** Leaving the tab leaves its screens: Back must not walk into another tab. */
  const goToTab = useCallback((next) => {
    if (nav.depth > 0) nav.reset();
    setTab(next);
  }, [nav]);
  const navDepthRef = useRef(0);
  navDepthRef.current = nav.depth;
  /**
   * Everything outgoing, persisted before it is attempted.
   *
   * wsService.send drops what it cannot deliver, so "sent" used to mean
   * "handed to a function that may have thrown it away" — for messages, read
   * receipts, handshakes and resend requests alike. Each of those is a job
   * now: written down first, retried with backoff, removed only when it
   * actually happened.
   */
  const jobsRef = useRef(null);
  if (!jobsRef.current) {
    jobsRef.current = new JobQueue({
      store: messageStore,
      canRun: () => wsService.isOpen(),
      handlers: {
        ws_send: async ({ frames }) => {
          if (!wsService.isOpen()) throw new Error('сокет закрыт');
          // Written, then WAITED FOR: the job is done when every copy has
          // its chat_sent back, not when the bytes left a socket that may
          // already be dead (sendAck.mjs). Receipts and other frames carry no
          // acknowledgement and go as before.
          const waits = (frames || [])
            .filter(f => f.type === 'chat_message' && f.clientMid)
            .map(f => expectAck(ackKey(f.clientMid, f.targetDeviceId), ACK_TIMEOUT_MS));
          for (const frame of frames || []) wsService.send(frame);
          try { await Promise.all(waits); } catch (e) {
            // No chat_sent on an open socket is the signature of a dead
            // path: ask the socket now, and retry on the one that replaces it.
            wsService.suspect('a send got no chat_sent'); throw e;
          }
        },
        /**
         * A message that could not be encrypted YET — kept whole, encrypted
         * when it can be.
         *
         * Different from ws_send on purpose: ws_send carries frames that
         * already exist, and a message blocked by an unconfirmed device has
         * none. It cannot: the frames for the devices we do trust would be a
         * partial send, which is the thing the block exists to prevent. So the
         * plaintext waits here and is encrypted to EVERY device at the moment
         * the person confirms — including the one that was refused.
         */
        chat_send: async ({ toUserId, clientMid, msgType, content, fileMeta, reply, fileId, replyToId }) => {
          const sessions = sessionsRef.current;
          if (!sessions) throw new Error('шифрование ещё не готово');
          const { send, blocked } = await encryptMessage(sessions, {
            verifyBundle: verifyBundleRef.current,
            toUserId, clientMid, msgType, content, fileMeta, reply,
            gossip: keyLogGossipRef.current,
          });
          if (blocked && blocked.length) {
            // Still waiting for the person, not failing. Holding keeps the
            // attempt budget for real failures — and everything queued behind
            // it for this conversation waits too, which is the point.
            throw holdError(`${blocked.length} устройство не подтверждено`);
          }
          if (!send.length) throw new Error('нет ни одного устройства для доставки');
          if (!wsService.isOpen()) throw new Error('сокет закрыт');
          const waits = send.map(p => expectAck(ackKey(p.clientMid, p.targetDeviceId), ACK_TIMEOUT_MS));
          for (const payload of send) {
            if (fileId) payload.fileId = fileId;
            if (replyToId) payload.replyToId = replyToId;
            wsService.send(payload);
          }
          try { await Promise.all(waits); } catch (e) { wsService.suspect('a send got no chat_sent'); throw e; }
        },
      },
    });
  }
  const earlyMessagesRef = useRef([]); // arrived before the ratchet was ready
  // Resolves when the ratchet is up. Sending during the second or two it takes
  // to publish a bundle used to fail outright — "no ratchet — refusing to send
  // unencrypted" — which is the right refusal for a broken device and the
  // wrong one for a device that is simply still starting.
  const sessionsReadyRef = useRef(null);
  if (!sessionsReadyRef.current) {
    let resolve;
    const p = new Promise((r) => { resolve = r; });
    sessionsReadyRef.current = { promise: p, resolve };
  }
  const [chatDeviceId, setChatDeviceId] = useState(null);
  // The push token usually arrives BEFORE the ratchet has published and the
  // device has a name, so it is registered without one and re-registered when
  // the name appears. Kept in refs because the bridge callbacks are installed
  // once and must read live values.
  const chatDeviceIdRef = useRef(null);
  /**
   * "May I talk to this device" — one answer, used by sending and receiving.
   *
   * Two answers is how the two ends of a session come to disagree about who is
   * allowed in it, which is the shape of the bug that let an unauthenticated
   * handshake through.
   *
   * `strict` is what makes it fail-closed, and it is on. An account with no
   * published signed list is refused rather than waved through — which was
   * only tolerable to switch on after the server's key material was wiped, so
   * that every account starts by publishing one. Before that it would have
   * refused everybody, including the owner.
   *
   * Being refused here is not the same as being offline: a list verified once
   * is cached and re-checked without asking anyone, so a server going quiet
   * cannot deny a device it has already vouched for.
   */
  const deviceGateRef = useRef(null);
  if (!deviceGateRef.current) {
    deviceGateRef.current = makeDeviceListGate(messageStore, api, {
      strict: true,
      onUnlisted: (d) => {
        console.warn('[CHAT] device', d.deviceId, 'of user', d.userId, 'is not in their signed list —', d.reason);
        if (d.masterChanged) setImpostorNotice(d);
      },
      // A list that changed is a decision that was made — by the account, not
      // at the banner. Everything held on "a device is unconfirmed" is worth
      // one more look; every queue, because the held message for user 5 was
      // waiting on a device of user 1 (one's own Web device).
      onRefreshed: (d) => {
        jobsRef.current.release()
          .then((n) => { if (n) { console.log('[CHAT] device list of user', d.userId, 'is now v' + d.version, '— releasing', n, 'held message(s)'); return jobsRef.current.runOnce(); } return null; })
          .catch(() => {});
      },
    });
  }
  // Set when the server's own record of published keys contradicts itself.
  // Sticky by design: the server that rewrote its history answers the next
  // request too, and a warning that clears itself is one nobody ever reads.
  const [keyLogAlarm, setKeyLogAlarm] = useState(null);
  // Our verified log head, ready to be sealed into every outgoing envelope.
  // Null until the chain has been walked — an unverified head is not gossip,
  // it is rumour.
  const keyLogGossipRef = useRef(null);
  useEffect(() => { keyLogAlarmStored(messageStore).then((a) => { if (a) setKeyLogAlarm(a); }).catch(() => {}); }, []);
  // A handshake that arrived claiming to be somebody it could not prove it
  // was. Sticky and stored: it is a statement about the server, and a warning
  // that vanishes on reload is one nobody reads.
  const [accountDeviceState, setAccountDeviceState] = useState(null);
  // One record per account, so a second contact in trouble does not erase the
  // first — and so that clearing one means deleting it, not writing an empty
  // husk that comes back for ever (services/trustNotices.mjs).
  const [impostorNotices, setImpostorNotices] = useState([]);
  const setImpostorNotice = useCallback((d) => {
    recordNotice(messageStore, d)
      .then((rec) => { if (rec) setImpostorNotices(prev => [rec, ...prev.filter(n => n.userId !== rec.userId)]); })
      .catch(() => {});
  }, []);
  const forgetImpostorNotice = useCallback((userId) => {
    setImpostorNotices(prev => prev.filter(n => n.userId !== Number(userId)));
    clearNotice(messageStore, userId).catch(() => {});
  }, []);
  useEffect(() => {
    loadNotices(messageStore).then(setImpostorNotices).catch(() => {});
  }, []);
  const pushTokensRef = useRef({ voip: null, alert: null });
  useEffect(() => {
    chatDeviceIdRef.current = chatDeviceId;
    if (!chatDeviceId) return;
    const { voip, alert } = pushTokensRef.current;
    if (voip) api.registerAPNsToken(voip, 'voip', chatDeviceId).catch(() => {});
    if (alert) api.registerAPNsToken(alert, 'alert', chatDeviceId).catch(() => {});
    // Android registers its own token natively and cannot see this value, so
    // it is handed over the bridge and the native side re-registers.
    try { window.VoxioxNative?.setChatDeviceId?.(chatDeviceId); } catch (e) { /* not native */ }
  }, [chatDeviceId]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [callPicker, setCallPicker] = useState(null); // { extension, userId, capabilities }
  const [noChoiceNotice, setNoChoiceNotice] = useState('');
  // Calls the server has told us are over. A replayed invite for one of them
  // must not ring.
  const endedCallIdsRef = useRef(new Set());

  // ─── Pull-to-refresh ──────────────────────────────────────
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const scrollContainerRef = useRef(null);

  // ─── ICE servers (TURN/STUN) ────────────────────────────────
  const [iceServers, setIceServers] = useState(null);

  // ─── Privacy mode ─────────────────────────────────────────
  const [privacyMode, setPrivacyMode] = useState(0);
  // 'e2ee' (default for everyone) | 'sip' | 'ask' — see settings.
  const [callProtocol, setCallProtocol] = useState('e2ee');
  const [privacyLoaded, setPrivacyLoaded] = useState(false);

  // ─── Update banner ─────────────────────────────────────────
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // ─── PWA install ─────────────────────────────────────────────
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // ─── Native Face ID ─────────────────────────────────────────
  const isNative = typeof window !== 'undefined' && !!window.VoxioxNative;
  const [nativeFaceIdLinked, setNativeFaceIdLinked] = useState(false);

  useEffect(() => {
    if (!isNative) return;
    window.VoxioxBridge = window.VoxioxBridge || {};
    window.VoxioxBridge.onAuthTokenCheck = (hasToken) => {
      setNativeFaceIdLinked(hasToken);
    };
    window.VoxioxBridge.onKeepAliveState = (enabled) => {
      setKeepAlive(enabled);
    };
    window.VoxioxNative.hasAuthToken();
    if (window.VoxioxNative.getKeepAlive) window.VoxioxNative.getKeepAlive();
    return () => {
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onAuthTokenCheck;
        delete window.VoxioxBridge.onKeepAliveState;
      }
    };
  }, [isNative]);

  const linkNativeFaceId = () => {
    const token = localStorage.getItem('voxiox_token');
    if (token && user && window.VoxioxNative.saveAuthToken) {
      window.VoxioxNative.saveAuthToken(token, user.username);
      setNativeFaceIdLinked(true);
    }
  };

  const unlinkNativeFaceId = () => {
    if (window.VoxioxNative.clearAuthToken) {
      window.VoxioxNative.clearAuthToken();
      setNativeFaceIdLinked(false);
    }
  };

  // Deep link /add-contact?name=X — prefill the friend search on the contacts tab.
  // ADR-013: we no longer auto-create anything (that leaked extensions and
  // bypassed consent); the user searches and sends a request explicitly.
  useEffect(() => {
    if (addContactProp && addContactProp.name) {
      setContactSearch(addContactProp.name);
      api.searchUsers(addContactProp.name).then(setSearchResults).catch(() => {});
    }
  }, []); // eslint-disable-line

  // Keep refs in sync
  // Publish this device and stock its prekeys, once per account.
  //
  // Before this resolves there is no way to encrypt, and sendMessage refuses
  // rather than falling back to plaintext — a fallback "just this once" is how
  // a private messenger stops being one.
  useEffect(() => {
    if (!user?.id) { sessionsRef.current = null; setChatDeviceId(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const s = new ChatSessions({
          identity: deviceIdentity,
          api,
          store: makeSessionStore(),
          myUserId: user.id,
          // Passed at construction on purpose. The send-side verifier is an
          // argument to each call, and it went a whole release being defined
          // and never handed over — the exact hole an audit later found on
          // the other side. A check nobody passes is a check that does not
          // exist, so this one cannot be forgotten at a call site.
          verifyIncoming: makeIncomingVerifier(tofu, {
            deviceIdOf,
            listDevices: (uid) => api.listDevices(uid),
            deviceList: deviceGateRef.current,
            onImpostor: (d) => {
              console.error('[CHAT] REFUSED a handshake claiming to be user', d.userId,
                'from device', d.deviceId, '—', d.reason);
              setImpostorNotice(d);
            },
          }),
        });
        const label = /iPhone|iPad/.test(navigator.userAgent) ? 'iOS'
          : /Android/.test(navigator.userAgent) ? 'Android' : 'Web';
        const deviceId = await s.publish({ label });
        if (cancelled) return;
        sessionsRef.current = s;
        // The one door in. Nothing else in this file may open a row: opening
        // spends the key, and three call sites spending it independently is
        // what turned read messages into errors
        // (docs/behavior/chat-message-lifecycle.md).
        intakeRef.current = new ChatIntake({
          sessions: s, archive: messageStore, myUserId: user.id,
          // Our half of the ratchet is gone and the far side cannot know it:
          // it believes the session is up and keeps sending, and every message
          // it sends is unreadable. So the first move is ours — drop the dead
          // session and send an empty message, which necessarily carries a
          // fresh handshake and rebuilds both directions. Signal does exactly
          // this in AutomaticSessionResetJob, rate limited for the same reason.
          onSessionLost: ({ peerUserId, deviceId }) => {
            rebuildSessionRef.current(peerUserId, deviceId);
          },
          // Rebuilding the session fixes what comes next; this recovers what
          // was already lost. The sender still holds the message in the clear
          // — its own copy — so it is the only side that can.
          // Gossip about the key log head arrives inside decrypted envelopes
          // (keyLogGossip.mjs). Our own copies carry our own head — only the
          // peer's word is worth checking.
          onOpened: (row) => {
            const g = row && row.key_log_gossip;
            if (!g || Number(row.from_user_id) === Number(user.id)) return;
            checkPeerHead(messageStore, { seq: g.s, head: g.h, from: row.from_user_id })
              .then((v) => {
                if (v.verdict === 'match') {
                  console.log('[KEYLOG] gossip: user', row.from_user_id, 'sees the same log (seq', g.s + ')');
                } else if (v.verdict === 'split') {
                  console.error('[KEYLOG] SPLIT VIEW: user', row.from_user_id, 'sees a different log at seq', g.s);
                  setKeyLogAlarm(v.alarm);
                } else if (v.verdict === 'ahead') {
                  // The peer has seen further — walk our own chain and let
                  // resolvePendingGossip pass judgement.
                  checkKeyLog(messageStore, api).then((r) => {
                    if (r.ok) keyLogGossipRef.current = currentGossip(r.state);
                    else setKeyLogAlarm(r);
                  }).catch(() => {});
                }
              })
              .catch(() => {});
          },
          onResendNeeded: ({ peerUserId, clientMid, deviceId }) => {
            jobsRef.current.enqueue('ws_send', {
              frames: [{ type: 'chat_resend_request', to: peerUserId, forClientMid: clientMid, deviceId }],
            }, { queue: `chat:${peerUserId}`, dedupeKey: `resend:${clientMid}` })
              .then(() => jobsRef.current.runOnce())
              .catch(() => {});
          },
        });
        setChatDeviceId(deviceId);
        // This account's own statement of which devices are its own. On a
        // fresh account the first device to get here mints the master and
        // signs a list of one — there is nothing to confirm, because the key
        // and the device come into existence together.
        try {
          // Pin our own account's master the same way we pin anybody's, through
          // the same call. One mechanism, so that a master which later CHANGES
          // means the same thing for our own account as it does for a contact
          // — and so that simply not holding it never gets mistaken for it.
          const mine = await syncPeer(messageStore, api, user.id);
          const pinned = (await peerMaster(messageStore, user.id))?.key || null;
          if (mine.masterChanged) {
            console.error('[DEVICES] this account is now signed by a different master key');
            setImpostorNotice({ userId: user.id, reason: 'ключ вашего аккаунта сменился', masterChanged: true });
          }
          const st = await accountState({ api, storage: secureSyncStorage, accountId: user.id, deviceId, pinnedMaster: pinned });
          if (st.state === ACCOUNT_STATE.FRESH) {
            const bundle = (await api.listDevices(user.id)).devices.find(d => d.deviceId === deviceId);
            if (bundle) {
              // deviceLabel from the endpoint, label in the list. Passing the
              // bundle straight through stored a null and left the linking
              // screen showing "Устройство без метки" for a phone that had one
              // — and recognising the device is the entire job of that screen.
              const made = await createAccountList({
                api, storage: secureSyncStorage, accountId: user.id,
                device: { deviceId: bundle.deviceId, identitySig: bundle.identitySig,
                          identityDh: bundle.identityDh, label: bundle.deviceLabel || null },
              });
              console.log('[DEVICES] account list created, version', made.version);
            }
          } else {
            console.log('[DEVICES] account list:', st.state, 'version', st.version || '-');
          }
          setAccountDeviceState(st.state);
        } catch (e) {
          console.warn('[DEVICES] could not settle the account device list:', e?.message || e);
        }
        // Walk the server's key log forward from the head we last vouched
        // for. TOFU sees a key that CHANGES under a name we know; this sees a
        // history that has been rewritten — a device added quietly, or one
        // removed from the record. It cannot see a server keeping two logs and
        // showing each of us a different one; that needs clients comparing
        // heads with each other (docs/plans/key-transparency.md).
        checkKeyLog(messageStore, api)
          .then((r) => {
            if (r.ok) {
              console.log('[KEYLOG] verified to seq', r.state.seq,
                r.added?.length ? `(+${r.added.length} new)` : '');
              keyLogGossipRef.current = currentGossip(r.state);
            } else {
              console.error('[KEYLOG] THE KEY LOG DOES NOT ADD UP:', r.error, 'at entry', r.at);
            }
            setKeyLogAlarm(r.ok ? null : r);
          })
          .catch((e) => console.warn('[KEYLOG] could not check:', e?.message || e));
        // The socket has been up for a moment already and does not know which
        // device it is. Until it does, the server routes our mail to whichever
        // socket of ours connected last — which is how a message encrypted for
        // the phone arrived at the browser, was skipped as somebody else's,
        // and turned up on the phone only at the next history fetch.
        wsService.announceDevice(deviceId);
        sessionsReadyRef.current.resolve(s);

        // Anything that arrived while this was starting.
        const early = earlyMessagesRef.current.splice(0);
        if (early.length) {
          console.log('[CHAT] opening', early.length, 'message(s) that arrived before the ratchet');
          for (const m of early) {
            intakeRef.current.accept(m).catch((e) =>
              console.warn('[CHAT] early message failed:', e?.message));
          }
        }
        console.log('[CHAT] ratchet ready, device', deviceId,
          isEvictable() ? '— keys are evictable here' : '— keys are in secure storage');
        // Which peer devices this one believes it has a session with. A chat
        // full of "не удалось расшифровать" means either no session or a
        // diverged one, and those are opposite problems.
        console.log('[CHAT] sessions held:',
          secureKeyNames().filter(k => k.includes(':session:')).map(k => k.split(':session:')[1]).join(', ') || 'none');
        // Once per launch, everything held on a person's decision is looked at
        // again: the trusted set may have changed anywhere since — a device
        // confirmed by voice in a call (ADR-014), a list adopted on another
        // screen, an app updated with a better rule. One look is not polling;
        // it costs at most one bundle fetch per still-unconfirmed device.
        jobsRef.current.release()
          .then((n) => { if (n) { console.log('[CHAT] launch: re-checking', n, 'held message(s)'); return jobsRef.current.runOnce(); } return null; })
          .catch(() => {});
      } catch (e) {
        console.error('[CHAT] could not bring the ratchet up:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => { chatUserRef.current = chatUser; }, [chatUser]);
  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { chatMessagesRef.current = chatMessages; }, [chatMessages]);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  useEffect(() => { sipConnectedRef.current = sipConnected; }, [sipConnected]);

  // ─── SW update listener ────────────────────────────────────
  //
  // The native shell updates through the APK or the App Store; the web app
  // INSIDE it updates from the server on every page load — and the app never
  // navigates, so that load happened whenever the WebView first opened and
  // never again. The banner that would have said so was hidden on native
  // ("updates via APK"), which is true of the shell and false of everything it
  // displays. A phone therefore ran whatever bundle it had when it last
  // started: fixes were deployed, tested against, and found still broken,
  // because the phone was running the code from before them.
  //
  // Reloading a WebView is cheap and the app has no unsaved state, so on
  // native it just reloads — but never during a call, where a reload would
  // drop the audio pipeline mid-sentence.
  useEffect(() => {
    const handler = () => {
      setUpdateAvailable(true);
      if (!isNative) return;
      const reloadWhenIdle = () => {
        if (activeCallRef.current || window._nativeCallActive) {
          console.log('[SW] update pending — waiting for the call to end');
          setTimeout(reloadWhenIdle, 5000);
          return;
        }
        console.log('[SW] new version — reloading the WebView');
        window.location.reload();
      };
      setTimeout(reloadWhenIdle, 1500);
    };
    window.addEventListener('swUpdate', handler);
    return () => window.removeEventListener('swUpdate', handler);
  }, [isNative]);

  // ─── Pull-to-refresh handlers ──────────────────────────────
  const onTouchStart = useCallback((e) => {
    if (refreshing) return;
    // Pull-to-refresh belongs to the tab underneath, and it reloads by tab. On
    // a pushed screen it would show a spinner and refetch something the person
    // is not looking at.
    if (navDepthRef.current > 0) return;
    const el = scrollContainerRef.current;
    if (el && el.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, [refreshing]);

  const onTouchMove = useCallback((e) => {
    if (!touchStartY.current || refreshing) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0) {
      setPullY(Math.min(dy * 0.4, 80));
    }
  }, [refreshing]);

  const onTouchEnd = useCallback(() => {
    if (pullY >= 60) {
      setRefreshing(true);
      setPullY(50);
      if (isNative) {
        // Native app: soft refresh (re-fetch data) instead of full page reload
        // Full reload in WebView can lose auth state during server deploys
        Promise.all([
          loadLinks(),
          tab === 'history' ? loadHistory() : Promise.resolve(),
          tab === 'users' ? Promise.all([loadUsers(), loadFriendRequests()]) : Promise.resolve(),
          tab === 'chat' ? Promise.all([loadUsers(), loadLastMessages()]) : Promise.resolve(),
          tab === 'more' && user?.role === 'admin' ? loadAdminUsers() : Promise.resolve(),
        ]).finally(() => {
          setRefreshing(false);
          setPullY(0);
        });
      } else {
        setTimeout(() => window.location.reload(), 400);
      }
    } else {
      setPullY(0);
    }
    touchStartY.current = 0;
  }, [pullY, isNative, tab, user]); // eslint-disable-line

  // ─── SIP connection ──────────────────────────────────────────
  useEffect(() => {
    if (!user?.sip_extension) return;
    async function connectSip() {
      try {
        const sipData = await api.sipCreds();
        // Store ICE servers for calls
        const servers = [{ urls: sipData.stun || `stun:${serverHostname()}:3478` }];
        if (sipData.turn) {
          servers.push({
            urls: sipData.turn.urls,
            username: sipData.turn.username,
            credential: sipData.turn.credential,
          });
        }
        setIceServers(servers);
        const mode = sipData.privacy_mode !== undefined ? sipData.privacy_mode : 0;
        setPrivacyMode(mode);
        setPrivacyLoaded(true);
        api.getSettings()
          .then(st => { if (st?.call_protocol) setCallProtocol(st.call_protocol); })
          .catch(() => { /* keep the e2ee default */ });

        // Cache SIP credentials in iOS Keychain for native calls on locked screen
        if (window.VoxioxNative?.isNative && window.VoxioxNative.cacheSIPCredentials) {
          window.VoxioxNative.cacheSIPCredentials(
            sipData.sip_extension,
            sipData.sip_password,
            serverHostname(),
            mode
          );
        }

        // Initialize native SIP (LinPhone) on iOS for earpiece/speaker control.
        // LinPhone registers via TLS as n{ext} (e.g., n81001) — separate native endpoint.
        // Only for iOS — Android uses JsSIP/WebRTC in WebView (audio routing works there).
        if (isIOS() && window.VoxioxNative?.initLinphone) {
          window.VoxioxNative.initLinphone();
          // Register after a short delay to let LinPhone Core initialize
          setTimeout(() => {
            window.VoxioxNative.registerSIP(
              sipData.sip_extension,
              sipData.sip_password,
              serverHostname()
            );
          }, 500);
          // Listen for registration status
          window.VoxioxBridge = window.VoxioxBridge || {};
          // On iOS, SIP is registered natively by LinPhone, and this callback
          // was logging that and telling nobody. The server derives "can this
          // peer take a SIP call" purely from a client's own sip_status, so an
          // iPhone whose SIP was registered and working reported itself as
          // app_no_sip — and the protocol picker, which needs both protocols
          // on offer, had nothing to offer. Asterisk knew all along; we never
          // asked it and the one client that knew never said.
          window.VoxioxBridge.onSIPRegistrationState = (state, msg) => {
            const registered = state === 'ok';
            console.log('[SIP-Native] LinPhone registration:', state, msg || '');
            nativeSipRef.current = registered;
            setSipConnected(registered || webSipRef.current);
            wsService.send({ type: 'sip_status', registered, native: true });
          };
        }

        // SIP always connects regardless of privacy_mode — needed for all call types
        const ua = sipService.connect({
          extension: sipData.sip_extension,
          password: sipData.sip_password,
          // Both, in order: /sip-ws on 443 (relay-capable) and Asterisk's own
          // 8089 as the fallback — JsSIP moves to the next when one will not
          // open (SEC-ARCH-007).
          wsUrls: process.env.REACT_APP_SIP_WS ? [process.env.REACT_APP_SIP_WS] : sipWsUrls(),
          domain: process.env.REACT_APP_SIP_DOMAIN || serverHostname(),
        });

        ua.on('registered', () => {
          console.log('[SIP] Registered successfully as', sipData.sip_extension);
          webSipRef.current = true;
          setSipConnected(true);
          wsService.send({ type: 'sip_status', registered: true, native: false });
        });
        ua.on('unregistered', () => {
          console.log('[SIP] Unregistered');
          webSipRef.current = false;
          setSipConnected(!!nativeSipRef.current);
          wsService.send({ type: 'sip_status', registered: false, native: false });
        });
        ua.on('registrationFailed', (e) => {
          console.error('[SIP] Registration FAILED for', sipData.sip_extension,
            '| cause:', e?.cause, '| status:', e?.response?.status_code, '| reason:', e?.response?.reason_phrase);
          webSipRef.current = false;
          setSipConnected(!!nativeSipRef.current);
          wsService.send({ type: 'sip_status', registered: false, native: false });
        });
        ua.on('connected', () => console.log('[SIP] WebSocket connected, ext:', sipData.sip_extension));
        ua.on('disconnected', (e) => {
          console.warn('[SIP] WebSocket disconnected | ext:', sipData.sip_extension,
            '| error:', e?.error, '| code:', e?.code, '| reason:', e?.reason);
          webSipRef.current = false;
          setSipConnected(!!nativeSipRef.current);
          wsService.send({ type: 'sip_status', registered: false, native: false });
        });
        ua.start();

        sipService.onIncoming((session) => {
          const callerExt = session.remote_identity?.uri?.user || 'Неизвестный';
          setIncomingOverlay({
            type: 'call',
            caller: callerExt,
            label: 'Входящий звонок',
            session,
            minimized: false,
          });
          session.on('ended', () => {
            setIncomingOverlay(prev => prev?.session === session ? null : prev);
          });
          session.on('failed', () => {
            setIncomingOverlay(prev => prev?.session === session ? null : prev);
          });
        });
      } catch (e) {
        console.error('SIP connect failed:', e);
      }
    }
    connectSip();
    return () => sipService.disconnect();
  }, [user]);

  // ─── WebSocket for chat + room notifications ──────────────────
  useEffect(() => {
    if (!privacyLoaded) return; // Wait for privacy mode to load from server
    const token = localStorage.getItem('voxiox_token');
    if (!token) return;
    wsService.connect(token, privacyMode);

    const unsub1 = wsService.on('chat_message', async (msg) => {
      const current = chatUserRef.current;
      // Opened here, once, and cached in plaintext locally. Everything
      // downstream — the screen, the cache, the chat list — deals in messages
      // and never learns that a ratchet exists.
      //
      // A copy addressed to another of our devices reaches this socket too:
      // the server delivers to the user, not to a device. It fails to open,
      // which is correct, and is dropped rather than shown as an error — the
      // real copy for this device arrives alongside it.
      const intake = intakeRef.current;
      if (!intake) {
        // The ratchet is still coming up. Dropping the message here left it
        // invisible until somebody opened that chat — the row was on the
        // server, but nothing on screen said anything had arrived. Signal
        // keeps an "early messages" cache for the same reason; ours only has
        // to survive a few seconds.
        earlyMessagesRef.current.push(msg);
        console.log('[CHAT] message arrived before the ratchet was up — holding it (',
          earlyMessagesRef.current.length, 'waiting )');
        return;
      }
      const opened = await intake.accept(msg);
      // Somebody else's copy: the server delivers to the user, not to a device.
      if (opened.skip) return;
      // A handshake carrier, not a message. It has done its work by arriving.
      if (opened.msgType === 'rekey') {
        console.log('[CHAT] handshake received from user', opened.from_user_id, '— session rebuilt');
        return;
      }
      if (current && opened.from_user_id === current.id) {
        setChatMessages(prev => (prev.some(m => m.id && m.id === opened.id) ? prev : [...prev, opened]));
        wsService.send({ type: 'chat_read', from: opened.from_user_id });
      } else {
        setUnreadCounts(prev => ({ ...prev, [opened.from_user_id]: (prev[opened.from_user_id] || 0) + 1 }));
      }
    });

    const unsub2 = wsService.on('chat_sent', (msg) => {
      // The server's word that this copy is stored — the send job waits for it.
      if (msg.clientMid) ackSend(ackKey(msg.clientMid, msg.target_device_id));
      // Which echo this is matters. One message is N ciphertexts, and only the
      // copy addressed to THIS device can be fetched and opened again later —
      // so that is the row id worth keeping, and its arrival is the moment the
      // sent message is safe to cache. Everything sent used to live in React
      // state alone: closing the chat lost it, and the server had no copy to
      // give back, so the sent half of a conversation disappeared every time.
      const mine = !!(msg.target_device_id && sessionsRef.current
        && msg.target_device_id === sessionsRef.current.deviceId);
      setChatMessages(prev => {
        // Match the echo to its optimistic message by the id WE chose. Matching
        // on content used to work only because the server echoed the text back;
        // under E2EE it cannot, and two ciphertexts of the same text differ
        // anyway, so every send would have left a duplicate on screen.
        // "Waiting" is not the only state a message can be reconciled out of.
        // One held back because a device was unconfirmed sits with sendError
        // and _pending false, and it goes out later, by itself, when the
        // person confirms — matching only on _pending left that echo
        // unmatched: the bubble kept saying "не отправлено" about a message
        // the server already had, and it was never cached under a real id.
        const awaiting = (m) => m._pending || m.sendError === 'E_UNTRUSTED' || m.sendError === 'E_RETRY';
        // The id we chose travels under two spellings: clientMid over the
        // socket, client_mid in a row from the database. Matching on only one
        // of them made a row loaded from history invisible to its own echo.
        const midOf = (m) => m.clientMid || m.client_mid;
        const idx = msg.clientMid
          ? prev.findIndex(m => awaiting(m) && midOf(m) === msg.clientMid)
          : prev.findIndex(m => awaiting(m) && (
              (m.file_id && m.file_id === msg.file_id) || (!m.file_id && m.content === msg.content)
            ));
        if (idx >= 0) {
          const updated = [...prev];
          // One message is now N ciphertexts, so N echoes come back. Keep what
          // is on screen — it is the plaintext we composed — and take only the
          // row id from the first echo. Replacing it with the echo would put
          // base64 in the bubble.
          // Our own copy replaces the temporary id — the placeholder is
          // truthy, so `id || msg.id` kept it for ever and the cached row
          // could never be matched against the same message from the server.
          const row = { ...updated[idx], _pending: false };
          delete row.sendError;   // it went out; the bubble must stop saying it did not
          if (mine && msg.id) row.id = msg.id;
          else if (!row.id) row.id = msg.id;
          updated[idx] = row;
          if (mine && msg.id) {
            messageStore.putMessages(
              conversationId(msg.from_user_id, msg.to_user_id), [{ ...row, _pending: false }],
            ).catch((e) => console.warn('[CHAT] could not cache sent message:', e?.message));
            // The server has it; the job that was sending it is done. The
            // echo is the acknowledgement — not the socket accepting a frame.
            jobsRef.current.remove(`msg:${msg.clientMid}`).catch(() => {});
          }
          return updated;
        }
        // An echo is an acknowledgement, not something that can be shown. Its
        // content is the ciphertext we just produced; we hold the plaintext,
        // or the row comes back through the intake pipeline, which is the only
        // thing allowed to open one. Appending it put a wall of base64 in the
        // conversation where the sender's own message should have been —
        // photographed on a phone, because the guard against it compared
        // clientMid to a history row that spells it client_mid.
        //
        // So: nothing to reconcile means nothing to do. Never render an
        // unopened row as if it were text.
        if (!prev.some(m => midOf(m) === msg.clientMid)) {
          console.warn('[CHAT] echo for', String(msg.clientMid || '').slice(0, 8),
            'has nothing on screen to reconcile — recovering the row from its job');
          recoverFromJob(msg);
        }
        return prev;
      });
    });

    /**
     * The echo came and the screen had forgotten the message (a reload in
     * between). The job that sent it still holds the row we composed: put
     * it back, with the server's id, and cache it — then the job is done.
     */
    const recoverFromJob = (msg) => {
      const mine = !!(msg.target_device_id && sessionsRef.current && msg.target_device_id === sessionsRef.current.deviceId);
      jobsRef.current.find(`msg:${msg.clientMid}`).then((job) => {
        const row = job?.payload?.row;
        if (!row) return;
        const shown = { ...row, _pending: false, id: (mine && msg.id) ? msg.id : row.id };
        delete shown.sendError;
        setChatMessages(prev => (prev.some(m => (m.clientMid || m.client_mid) === msg.clientMid) ? prev : [...prev, shown]));
        if (mine && msg.id) {
          messageStore.putMessages(conversationId(msg.from_user_id, msg.to_user_id), [shown]).catch(() => {});
          jobsRef.current.remove(`msg:${msg.clientMid}`).catch(() => {});
        }
      }).catch(() => {});
    };

    const unsub5 = wsService.on('chat_read', (msg) => {
      // msg = { type: 'chat_read', by: userId } — recipient read our messages
      setChatMessages(prev => prev.map(m =>
        (m.from_user_id === user?.id && m.to_user_id === msg.by) ? { ...m, read: 1 } : m
      ));
    });

    const unsub3 = wsService.on('room_join', (msg) => {
      const notification = { room: msg.room, label: msg.label, code: msg.code, timestamp: Date.now() };
      setIncomingOverlay(prev => {
        if (prev) return prev;
        // Don't show overlay if already in a call
        if (activeCallRef.current) return null;
        // Don't show overlay if native E2EE call is active (CallKit handles it)
        if (window._nativeCallActive) return null;
        return { type: 'room', caller: msg.label || 'Звонок по ссылке', label: 'Кто-то ждёт в комнате', room: notification };
      });
      setRoomNotifications(prev => {
        if (prev.find(n => n.room === msg.room)) return prev;
        return [...prev, notification];
      });
      // Update link status
      if (msg.code) setLinkStatuses(prev => ({ ...prev, [msg.code]: { status: 'guest_waiting' } }));
      // Native CallKit (iOS) — show system call screen (only if no native call active)
      if (window.VoxioxNative?.reportIncomingCall && !window._nativeCallActive) {
        window.VoxioxNative.reportIncomingCall(msg.label || 'Входящий звонок');
      }
    });

    // Active rooms sent on WS auth (for push-opened app or reconnection)
    const unsub4 = wsService.on('active_rooms', (msg) => {
      const rooms = msg.rooms || [];
      rooms.forEach(r => {
        setRoomNotifications(prev => {
          if (prev.find(n => n.room === r.room)) return prev;
          return [...prev, { room: r.room, label: r.label, code: r.code, timestamp: r.timestamp }];
        });
        if (r.code) setLinkStatuses(prev => ({ ...prev, [r.code]: { status: 'guest_waiting' } }));
      });
    });

    // Refetch missed data after WS reconnection (dead connection gap recovery)
    const unsub6 = wsService.on('ws_reconnected', () => {
      console.log('[WS] Reconnected — refetching missed messages, re-syncing SIP status:', sipConnectedRef.current);
      jobsRef.current.runOnce().catch(() => {});
      // Re-send SIP status so server knows about JsSIP registration on the new WS
      // Re-state it on every reconnect. The client reports registration only
      // when it CHANGES, so a socket that came back knew nothing until the
      // next change — which on a stable registration is never.
      api.getUnreadCounts().then(counts => {
        const map = {};
        counts.forEach(c => { map[c.from_user_id] = c.count; });
        setUnreadCounts(map);
      }).catch(() => {});
      // Refetch current chat if open
      const current = chatUserRef.current;
      if (current) {
        // A reload keeps what is still on its way to the server (chatMerge.mjs).
        fetchMessages(current.id)
          .then(({ messages }) => setChatMessages(prev => { console.log('[CHAT] reload after reconnect:', messages.length, 'rows, screen had', prev.length); return keepUnacknowledged(prev, messages); }))
          .catch((e) => console.warn('[CHAT] reload after reconnect failed:', e?.message));
      }
    });

    api.getUnreadCounts().then(counts => {
      const map = {};
      counts.forEach(c => { map[c.from_user_id] = c.count; });
      setUnreadCounts(map);
    }).catch(() => {});

    // Fetch active rooms on mount (in case push opened app)
    api.getActiveRooms().then(rooms => {
      rooms.forEach(r => {
        setRoomNotifications(prev => {
          if (prev.find(n => n.room === r.room)) return prev;
          return [...prev, { room: r.room, label: r.label, code: r.code, timestamp: r.timestamp }];
        });
        if (r.code) setLinkStatuses(prev => ({ ...prev, [r.code]: { status: 'guest_waiting' } }));
      });
    }).catch(() => {});

    // ─── Direct E2EE call listeners ────────────────────────────
    const unsub7 = wsService.on('direct_call_room', (msg) => {
      // Server assigned a room for our outgoing E2EE call
      console.log('[E2EE-DIAG] Got room assignment:', msg.callId, 'room:', msg.roomNumber);
      setActiveCall(prev => {
        if (!prev || prev.callId !== msg.callId) return prev;
        return { ...prev, waitingForRoom: false, roomNumber: msg.roomNumber, directCallId: msg.callId };
      });
    });

    const showDirectCallOverlay = (msg) => {
      // A call the server has already finished with. The invite can arrive
      // late — a frozen WebView replays its backlog on waking — and there is
      // nothing in the message itself that says how old it is.
      if (msg.callId && endedCallIdsRef.current.has(msg.callId)) {
        console.log('[CALL] ignoring invite for a call that already ended:', msg.callId);
        return;
      }
      // Obey the protocol the caller chose. This used to be hardcoded to
      // 'direct_e2ee', so the receiving side always assumed the tunnel no
      // matter how the call had actually been placed.
      const protocol = msg.protocol === 'sip' ? 'sip' : 'e2ee';
      console.log('[CALL] incoming', msg.callId, 'protocol:', protocol, 'from:', msg.from);
      setIncomingOverlay(prev => {
        if (prev) return prev; // dedup
        return {
          type: protocol === 'sip' ? 'direct_sip' : 'direct_e2ee',
          protocol,
          caller: msg.fromUsername || (protocol === 'sip' ? 'Входящий звонок' : 'Зашифрованный звонок'),
          callerId: msg.from,
          callId: msg.callId,
          roomNumber: msg.roomNumber,
          label: protocol === 'sip' ? 'Входящий звонок' : 'Зашифрованный звонок',
        };
      });
    };

    const handleDirectCallInvite = (msg) => {
      console.log('[E2EE-DIAG] direct_call_invite received:', msg.callId, 'from:', msg.from, 'room:', msg.roomNumber,
        'activeCall:', !!activeCallRef.current, 'nativeCallActive:', !!window._nativeCallActive);
      // Busy is an answer, not silence. Dropping the invite here left the
      // caller listening to ringing for the full 45 seconds for a call that
      // was never going to be picked up (И-7).
      if (activeCallRef.current || window._nativeCallActive) {
        console.log('[CALL] busy — declining invite', msg.callId,
          'activeCall:', !!activeCallRef.current, 'native:', !!window._nativeCallActive);
        if (msg.callId) wsService.send({ type: 'direct_call_decline', callId: msg.callId, reason: 'busy' });
        return;
      }

      // iOS race condition fix: when screen unlocks, JS unfreezes and processes
      // buffered WS messages BEFORE evaluateJavaScript("window._nativeCallActive=true")
      // has a chance to execute. Delay on iOS to let the flag propagate.
      if (window.VoxioxNative) {
        console.log('[E2EE-DIAG] iOS: delaying invite processing (300ms) for native flag');
        setTimeout(() => {
          if (window._nativeCallActive) { console.log('[E2EE-DIAG] Native call became active during delay, ignoring invite'); return; }
          if (activeCallRef.current) { console.log('[E2EE-DIAG] Already in call after delay, ignoring invite'); return; }
          showDirectCallOverlay(msg);
        }, 300);
        return;
      }

      showDirectCallOverlay(msg);
    };
    const unsub8 = wsService.on('direct_call_invite', handleDirectCallInvite);

    // Native WS bridge: when Android native WS receives direct_call_invite while
    // JS WS is reconnecting, NativeWSClient forwards it via bridge.evaluateJS
    // The protocol travels with the invite (И-3). Leaving it off here made
    // every call forwarded through the native socket look like a tunnel call,
    // however it had actually been placed.
    window._onNativeDirectCallInvite = (from, fromUsername, callId, roomNumber, protocol) => {
      console.log('[E2EE-DIAG] Native WS forwarded direct_call_invite:', from, callId, roomNumber, 'protocol:', protocol);
      handleDirectCallInvite({ from, fromUsername, callId, roomNumber, protocol });
    };

    const unsub9 = wsService.on('direct_call_declined', (msg) => {
      if (msg.callId) endedCallIdsRef.current.add(msg.callId);
      console.log('[CALL] declined:', msg.callId, 'reason:', msg.reason || 'declined');
      let matched = false;
      setActiveCall(prev => {
        if (!prev || (prev.callId || prev.directCallId) !== msg.callId) return prev;
        matched = true;
        return null;
      });
      if (matched && msg.reason === 'busy') alert('Абонент занят другим разговором');
    });

    // The caller put the handset down while we were still ringing. Nothing used
    // to say so, and the phone rang on for the full 45 seconds.
    const unsub9b = wsService.on('direct_call_cancelled', (msg) => {
      if (msg.callId) endedCallIdsRef.current.add(msg.callId);
      // Not always the caller any more: another device of ours may have taken
      // the call or turned it down, and this is how the rest stop ringing.
      console.log('[CALL] ringing stopped:', msg.callId, 'reason:', msg.reason || 'cancelled_by_caller');
      if (window.VoxioxNative?.dismissIncomingNotification) {
        window.VoxioxNative.dismissIncomingNotification();
      }
      setIncomingOverlay(prev => (prev && prev.callId === msg.callId) ? null : prev);
    });

    // Remember that the peer took the call: cancelling afterwards is a hangup,
    // and hangups travel through the tunnel, not through this message.
    const unsub9c = wsService.on('direct_call_accepted', (msg) => {
      setActiveCall(prev => {
        if (!prev || (prev.callId || prev.directCallId) !== msg.callId) return prev;
        return { ...prev, accepted: true };
      });
    });

    // We pressed Answer and there was nothing left to answer. Without this the
    // screen went on to join a tunnel that refused it, and the person read
    // "Not authorized for this room".
    const unsub9d = wsService.on('direct_call_accept_failed', (msg) => {
      if (msg.callId) endedCallIdsRef.current.add(msg.callId);
      console.log('[CALL] accept failed:', msg.callId, 'reason:', msg.reason);
      setIncomingOverlay(prev => (prev && prev.callId === msg.callId) ? null : prev);
      setActiveCall(prev => {
        if (!prev || (prev.callId || prev.directCallId) !== msg.callId) return prev;
        return null;
      });
      // alert, not the banner: the banner lives on the calls tab, and someone
      // answering a call is usually looking at a chat.
      alert({
        timeout: 'Звонок больше не активен — звонящий не дождался',
        cancelled: 'Звонящий отменил вызов',
        declined: 'Звонок уже был отклонён',
        busy: 'Абонент был занят другим разговором',
        ended: 'Звонок уже завершён',
      }[msg.reason] || 'Не удалось принять звонок');
    });

    const unsub10 = wsService.on('direct_call_unavailable', (msg) => {
      // The server distinguishes "nowhere to ring" from "we cannot ring" —
      // the second is our own misconfiguration and must not be reported as the
      // peer being absent.
      console.log('[CALL] unavailable:', msg.callId, 'reason:', msg.reason || 'unspecified');
      setActiveCall(prev => {
        if (!prev || prev.callId !== msg.callId) return prev;
        return null;
      });
      alert(msg.reason === 'push_unconfigured'
        ? 'Не удалось дозвониться: уведомления на сервере не настроены'
        : 'Абонент недоступен — нет ни активного приложения, ни уведомлений');
    });

    // Ringing ended without an answer. Previously nothing arrived and the call
    // screen simply stayed up: a phone was seen counting seconds for a call the
    // server had already dropped.
    const unsub10b = wsService.on('direct_call_timeout', (msg) => {
      if (msg.callId) endedCallIdsRef.current.add(msg.callId);
      console.log('[CALL] timeout:', msg.callId);
      setActiveCall(prev => {
        if (!prev || prev.callId !== msg.callId) return prev;
        return null;
      });
      alert('Абонент не отвечает');
    });

    // Calls that were already ringing when this client connected. A device woken
    // by push arrives here: the invite went out while it had no socket, so the
    // server hands it over on auth instead.
    const unsub10c = wsService.on('auth_ok', (msg) => {
      // Every authenticated socket is told the SIP state — the first one too.
      // JsSIP registered 130 ms before the socket finished authenticating, the
      // report went to a socket the server would not listen to yet, and the
      // phone stayed app_no_sip until the next change (Pixel, 03.09.2026).
      for (const report of sipStatusReports({ web: webSipRef.current, native: nativeSipRef.current })) wsService.send(report);
      const pending = msg.pendingCalls || [];
      // auth_ok is the authoritative answer to "what is ringing right now".
      //
      // A WebView that was frozen wakes with the socket's backlog still queued
      // — including the invite for a call that has since been answered
      // elsewhere, declined, or timed out. It played that backlog and put up a
      // ringing screen for a call that no longer existed; pressing Answer got
      // "звонок уже завершён", which was at least honest. Anything not on this
      // list is over, whatever the backlog says.
      const live = new Set(pending.map(c => c.callId));
      setIncomingOverlay(prev => {
        if (!prev || !prev.callId || live.has(prev.callId)) return prev;
        console.log('[CALL] dropping stale ringing screen for', prev.callId);
        return null;
      });
      if (window.VoxioxNative?.dismissIncomingNotification) {
        // The native ringer has the same backlog problem.
        if (!pending.length) window.VoxioxNative.dismissIncomingNotification();
      }
      if (!pending.length) return;
      console.log('[CALL] pending on connect:', pending.map(c => c.callId));
      for (const c of pending) {
        handleDirectCallInvite({
          from: c.from, fromUsername: c.fromUsername,
          callId: c.callId, roomNumber: c.roomNumber, protocol: c.protocol,
        });
      }
    });

    // The server refuses messages to non-friends (ADR-013). Without this the
    // message just vanished and the chat looked broken.
    /**
     * Somebody could not open a message we sent, and is asking for it again.
     *
     * We can answer because the sender keeps its own copy in the clear. The
     * resend goes out under a NEW row id with the ORIGINAL id inside the
     * envelope: the server collapses a repeat by client_mid, so reusing it
     * would be acknowledged and never delivered, and the receiver still has to
     * recognise this as the message it asked for.
     */
    const unsubResend = wsService.on('chat_resend_request', async (msg) => {
      const sessions = sessionsRef.current;
      if (!sessions || !msg.forClientMid) return;
      try {
        const conv = conversationId(user.id, msg.from);
        const held = await messageStore.getMessages(conv);
        const original = held.find(m => m.client_mid === msg.forClientMid
          && Number(m.from_user_id) === Number(user.id) && !m.decryptError);
        if (!original) {
          console.warn('[CHAT] user', msg.from, 'asked for message', String(msg.forClientMid).slice(0, 8),
            '— this device does not hold it any more');
          return;
        }
        console.log('[CHAT] resending message', String(msg.forClientMid).slice(0, 8), 'to user', msg.from);
        const { send, blocked } = await encryptMessage(sessions, {
          verifyBundle: verifyBundleRef.current,
          toUserId: msg.from,
          clientMid: msg.forClientMid,                       // inside the envelope
          outerMid: (crypto.randomUUID?.() || `resend-${Date.now()}`),
          gossip: keyLogGossipRef.current,
          msgType: original.msgType || original.type || 'text',
          content: original.content || '',
          // The whole file block, not just the id: a resent sealed
          // attachment without its key is a blob the recipient can never
          // open (§7в п.3).
          fileMeta: original.file_id ? {
            id: original.file_id,
            ...(original.file_key ? {
              k: original.file_key, name: original.file_name, mime: original.file_mime,
              size: original.file_size, dur: original.file_duration,
            } : {}),
          } : null,
        });
        if (blocked && blocked.length) {
          console.warn('[CHAT] cannot resend: an unconfirmed device is in the way');
          return;
        }
        for (const payload of send) {
          if (original.file_id) payload.fileId = original.file_id;
          wsService.send(payload);
        }
      } catch (e) {
        console.warn('[CHAT] resend failed:', e?.message);
      }
    });

    const unsubChatErr = wsService.on('chat_error', (msg) => {
      setLoadError(msg.message || 'Сообщение не отправлено');
      setTimeout(() => setLoadError(''), 6000);
      if (msg.reason === 'not_friends') {
        // The thread is stale — refresh the list so it disappears from the UI.
        loadLastMessages();
        loadUsers();
      }
    });

    const unsubFriendReq = wsService.on('friend_request', () => {
      loadFriendRequests();
    });

    const unsub11 = wsService.on('user_status', (msg) => {
      setUserStatuses(prev => ({ ...prev, [msg.userId]: msg.clientType }));
    });

    return () => {
      unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); unsub6(); unsub7(); unsub8(); unsub9(); unsub9b(); unsub9c(); unsub9d(); unsub10(); unsub10b(); unsub10c(); unsub11(); unsubChatErr(); unsubResend(); unsubFriendReq();
      delete window._onNativeDirectCallInvite;
      wsService.disconnect();
    };
  }, [privacyLoaded]); // eslint-disable-line

  // ─── Push notifications subscription ──────────────────────────
  useEffect(() => {
    // Only auto-subscribe if permission already granted (avoid Safari user-gesture error)
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      subscribePush();
    }
  }, []); // eslint-disable-line

  async function subscribePush() {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = await api.getVapidKey();
      if (!vapidKey) return;
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
        return outputArray;
      };
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) });
      await api.pushSubscribe(sub.toJSON());
    } catch (e) {}
  }

  // ─── APNs device token registration (iOS VoIP push) ─────────
  useEffect(() => {
    if (!privacyLoaded || !window.VoxioxNative?.isNative) return;

    window.VoxioxBridge = window.VoxioxBridge || {};

    // Register VoIP push device token with server (calls)
    window.VoxioxBridge.onDeviceToken = (token) => {
      if (token) {
        pushTokensRef.current.voip = token;
        api.registerAPNsToken(token, 'voip', chatDeviceIdRef.current).catch(() => {});
      }
    };
    window.VoxioxNative.getDeviceToken();

    // Alert push token (messages, contact requests) — a different APNs channel.
    // The token may not exist yet on first launch (the permission prompt is
    // still up), so retry a few times before giving up.
    let lastAlertToken = null;
    let alertPollTimer = null;
    window.VoxioxBridge.onAlertPushToken = (token) => {
      if (!token || token === lastAlertToken) return;
      lastAlertToken = token;
      if (alertPollTimer) { clearTimeout(alertPollTimer); alertPollTimer = null; }
      pushTokensRef.current.alert = token;
      api.registerAPNsToken(token, 'alert', chatDeviceIdRef.current).catch(() => {});
    };
    // Tapping a notification must land in the right conversation. The native
    // side delivers it live, or hands it over on first ask after a cold start.
    window.VoxioxBridge.onOpenChat = async (peerId) => {
      const id = parseInt(peerId, 10);
      if (!Number.isInteger(id)) return;
      let peer = usersRef.current.find(u => u.id === id);
      if (!peer) {
        // Freshly accepted contact the list has not caught up with yet.
        try { peer = (await api.getFriends()).find(u => u.id === id); } catch (e) { /* offline */ }
      }
      if (peer) openChatRef.current?.(peer);
    };
    if (window.VoxioxNative.getPendingChat) window.VoxioxNative.getPendingChat();

    // Re-register whenever the app comes back. Registration used to happen once
    // per page load, and the WebView is not reloaded when the app is reopened —
    // so a token dropped server-side (expired, cleaned up, database restored)
    // was never sent again and pushes stayed dead until a full restart.
    window.__voxioxReaskAlertToken = () => {
      lastAlertToken = null;
      window.VoxioxNative?.getAlertPushToken?.();
    };
    if (window.VoxioxNative.getAlertPushToken) {
      // The token is empty until APNs answers (and until the user accepts the
      // permission prompt), so poll a few times, then stop.
      let attempts = 0;
      const askForAlertToken = () => {
        window.VoxioxNative.getAlertPushToken();
        if (++attempts < 5) alertPollTimer = setTimeout(askForAlertToken, 3000);
      };
      askForAlertToken();
    }

    // Handle native E2EE call display (screen unlocked during active native tunnel)
    window.VoxioxBridge.onNativeCallDisplay = (info) => {
      console.log('[E2EE-DIAG] onNativeCallDisplay:', info);
      // Show CallRoom in "native display" mode — no tunnel, no getUserMedia,
      // just render the call UI with SAS, mute/speaker/end buttons.
      setIncomingOverlay(null); // Clear any stale overlay
      setActiveCall({
        extension: info.caller || 'E2EE',
        callProtocol: 'e2ee',
        roomNumber: info.room,
        nativeDisplayMode: true,  // ← special flag for CallRoom
        nativeSAS: info.sas,      // hex string e.g. "269806d5"
        nativeElapsed: info.elapsed || 0,
        nativeMuted: info.muted || false,
      });
    };

    // Handle native call ended (native tunnel ended, may fire deferred from background)
    window.VoxioxBridge.onNativeCallEnded = () => {
      console.log('[E2EE-DIAG] onNativeCallEnded');
      // Clear stale incoming overlay — it was set by room_join before user
      // answered via CallKit, and never dismissed because answer was native.
      setIncomingOverlay(null);
      // If currently showing native display mode CallRoom, close it
      if (activeCallRef.current?.nativeDisplayMode) {
        setActiveCall(null);
        setCallMinimized(false);
      }
    };

    // A call the person already accepted, waiting for the app to catch up: the
    // phone was locked or killed, CallKit took the answer, and JS is only now
    // able to connect it.
    //
    // Connecting is all this does, so the native side must hand over accepted
    // calls and nothing else — a ringing call arriving here is answered by the
    // act of arriving (И-1, docs/behavior/call-lifecycle.md). The guards below
    // are the second line: a call already in progress must not be replaced.
    window.VoxioxBridge.onPendingCall = (room, code, callId) => {
      if (!room) return;
      console.log('[CALL] pending handover from native: room=', room, 'callId=', callId);
      if (activeCallRef.current) { console.log('[CALL] pending handover skipped — already in call'); return; }
      if (window._nativeCallActive) { console.log('[CALL] pending handover skipped — native call active'); return; }
      if (callId) {
        wsService.send({ type: 'direct_call_accept', callId });
        setActiveCall({ extension: 'E2EE', callProtocol: 'e2ee', roomNumber: room, directCallId: callId });
      } else {
        const adminExt = '9' + String(room).slice(1);
        setActiveCall({ extension: adminExt, roomJoin: true, linkCode: code, roomNumber: room });
      }
      setIncomingOverlay(null);
    };
    window.VoxioxNative.getPendingCall();

    // The native client saw the caller give up. It has already taken its own
    // notification down; the WebView may still be showing an overlay.
    window.VoxioxBridge.onDirectCallCancelled = (callId) => {
      console.log('[CALL] cancelled by caller (via native):', callId);
      setIncomingOverlay(prev => (prev && (!callId || prev.callId === callId)) ? null : prev);
    };

    // Handle push call answer (VoIP push answered via notification/CallKit)
    window.VoxioxBridge.onPushCallAnswer = (room, code, callId) => {
      if (room) {
        console.log('[E2EE-DIAG] onPushCallAnswer: room=', room, 'code=', code, 'callId=', callId);
        if (activeCallRef.current) {
          console.log('[E2EE-DIAG] onPushCallAnswer: skipping — already in call');
          return;
        }
        if (window._nativeCallActive) {
          console.log('[E2EE-DIAG] onPushCallAnswer: skipping — native call active');
          return;
        }
        if (callId) {
          // E2EE direct call — need to accept and join tunnel with directCallId
          wsService.send({ type: 'direct_call_accept', callId });
          setActiveCall({
            extension: 'E2EE',
            callProtocol: 'e2ee',
            roomNumber: room,
            directCallId: callId,
          });
        } else if (room >= 90000) {
          // A direct call with no identifier cannot be accepted, and the
          // branch below would dial SIP extension "90000" — which does not
          // exist, so the call died as "Rejected" with nothing explaining it.
          // Say so instead of inventing a call.
          console.error('[CALL] direct call answered without a callId — cannot accept, room:', room);
          alert('Не удалось принять звонок: не пришёл идентификатор. Откройте приложение и перезвоните.');
        } else {
          // Link-based call — join room normally
          const adminExt = '9' + String(room).slice(1);
          setActiveCall({ extension: adminExt, roomJoin: true, linkCode: code, roomNumber: room });
        }
        setIncomingOverlay(null);
      }
    };

    return () => {
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onDeviceToken;
        delete window.VoxioxBridge.onPendingCall;
        delete window.VoxioxBridge.onPushCallAnswer;
        delete window.VoxioxBridge.onDirectCallCancelled;
        delete window.VoxioxBridge.onNativeCallDisplay;
        delete window.VoxioxBridge.onNativeCallEnded;
      }
    };
  }, [privacyLoaded]); // eslint-disable-line

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (tab === 'calls') loadLinks();
      // Re-check active rooms on focus (push notification may have opened app)
      api.getActiveRooms().then(rooms => {
        rooms.forEach(r => {
          setRoomNotifications(prev => {
            if (prev.find(n => n.room === r.room)) return prev;
            return [...prev, { room: r.room, label: r.label, code: r.code, timestamp: r.timestamp }];
          });
          if (r.code) setLinkStatuses(prev => prev[r.code] ? prev : { ...prev, [r.code]: { status: 'guest_waiting' } });
        });
      }).catch(() => {});
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [tab]); // eslint-disable-line

  // ─── Data loaders ────────────────────────────────────────────
  const loadLinks = useCallback(async () => { setLoadError(''); try { setLinks(await api.getLinks()); } catch (e) { setLoadError(e.message); } }, []);
  const loadHistory = useCallback(async () => { try { const r = await api.getHistory(); setHistory(r.calls); } catch (e) {} }, []);
  const loadUsers = useCallback(async () => { try { setUsers(await api.getUsers()); } catch (e) {} }, []);
  // The server has always sent {incoming, outgoing}; only the first half was
  // read. So a request you sent vanished — no confirmation, no way to check it,
  // no way to take it back short of searching for the person again.
  const loadFriendRequests = useCallback(async () => {
    try {
      const r = await api.getFriendRequests();
      setIncomingRequests(r.incoming || []);
      setOutgoingRequests(r.outgoing || []);
    } catch (e) {}
  }, []);
  const loadBlocked = useCallback(async () => {
    try { setBlockedUsers(await api.getBlocked()); } catch (e) {}
  }, []);
  // The chat list preview is a message like any other, so it has to be opened
  // like any other — otherwise the list shows base64 under every name.
  const loadLastMessages = useCallback(async () => {
    try {
      const sessions = sessionsRef.current;
      const rows = await api.getLastMessages(sessions?.deviceId || null);
      if (!sessions) { setLastMessages(rows); return; }
      // The preview does NOT decrypt. It used to, on every refresh of the
      // list — which spent the key of the newest message before the
      // conversation ever asked for it, and the conversation then showed an
      // error for a message the list had just displayed fine. The archive is
      // the only source: anything not in it yet is simply not opened yet.
      const opened = await Promise.all(rows.map(async (r) => {
        const conv = conversationId(user.id, r.from_user_id === user.id ? r.to_user_id : r.from_user_id);
        if (r.enc_v !== 1) {
          // The preview is the second place a plaintext row could be drawn
          // as text; it follows the same cutoff the intake keeps (§7е).
          const meta = await withTimeout(messageStore.getMeta(conv), CACHE_TIMEOUT_MS, {});
          const code = legacyVerdict(meta && meta.legacyCutoff, r);
          return code ? { ...r, content: DECRYPT_ERROR_TEXT[code], msgType: 'text', decryptError: code } : r;
        }
        const held = await withTimeout(messageStore.getMessages(conv), CACHE_TIMEOUT_MS, []);
        const hit = held.find(m => m && m.id === r.id && !m.decryptError);
        return hit ? { ...r, ...hit } : { ...r, content: 'Новое сообщение', msgType: 'text' };
      }));
      setLastMessages(opened);
    } catch (e) {}
  }, []);
  // The list is paged and searched on the server now; the old screen asks for
  // the first hundred, which is what it always drew anyway.
  const loadAdminUsers = useCallback(async () => {
    try { setAdminUsers((await api.adminGetUsers({ limit: 100 })).items || []); }
    catch (e) { /* the settings screen survives without it */ }
  }, []);

  // The icon badge counts conversations, matching both the chat list and what
  // the server puts in the push — three places that must never disagree.
  useEffect(() => {
    if (!window.VoxioxNative?.setBadge) return;
    const unreadChats = Object.values(unreadCounts).filter(n => n > 0).length;
    window.VoxioxNative.setBadge(unreadChats);
  }, [unreadCounts]);

  // A backgrounded WebView is frozen: the WebSocket stays open, so 'ws_reconnected'
  // never fires and anything that arrived meanwhile is silently missed. Refetch
  // whenever the app becomes visible again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      loadLastMessages();
      loadUsers();
      loadFriendRequests();
      window.__voxioxReaskAlertToken?.();
      window.VoxioxNative?.getPendingChat?.();
      api.getUnreadCounts().then(counts => {
        const map = {};
        counts.forEach(c => { map[c.from_user_id] = c.count; });
        setUnreadCounts(map);
      }).catch(() => {});
      const current = chatUserRef.current;
      if (current) {
        fetchMessages(current.id)
          .then(({ messages }) => setChatMessages(prev => { console.log('[CHAT] reload on visible:', messages.length, 'rows, screen had', prev.length); return keepUnacknowledged(prev, messages); }))
          .catch((e) => console.warn('[CHAT] reload on visible failed:', e?.message));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadLastMessages, loadUsers, loadFriendRequests]);

  useEffect(() => {
    loadLinks();
    if (tab === 'history') loadHistory();
    if (tab === 'users') { loadUsers(); loadFriendRequests(); loadBlocked(); }
    if (tab === 'chat') { loadUsers(); loadLastMessages(); }
    if (tab === 'more' && user?.role === 'admin') loadAdminUsers();
  }, [tab]); // eslint-disable-line

  // ─── Actions ─────────────────────────────────────────────────
  const createLink = async () => {
    setLoading(true);
    try {
      const link = await api.createLink(newLabel || undefined, ttlHours, undefined, maxParticipants);
      setLinks(prev => [link, ...prev]);
      setNewLabel(''); setMaxParticipants(2); setTtlHours(24); setShowCreateSheet(false);
    } catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(url); } catch (e) {
      try { const ta = document.createElement('textarea'); ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); setCopied(url); } catch (e2) {}
    }
    setTimeout(() => setCopied(''), 2000);
  };

  const deleteLink = async (code) => {
    // Deleting a link breaks it for everybody who has it. Said, rather than
    // asked in four words over a modal.
    if (!window.confirm('Удалить ссылку? Она перестанет работать у всех, кому вы её отправляли.')) return;
    try { await api.deleteLink(code); setLinks(prev => prev.filter(l => l.code !== code)); } catch (e) { alert('Ошибка: ' + e.message); }
  };

  // ─── Smart call: detect capabilities, show protocol picker ──
  const callUser = (ext, userId) => {
    if (!userId) {
      // Fallback: direct SIP if no userId (e.g., external contacts)
      setActiveCall({ extension: ext });
      return;
    }
    // No local veto. Whether the peer is reachable is the server's answer, not
    // ours: it knows about push tokens, and a phone with no socket is exactly
    // the case push exists for. Refusing here made a backgrounded Android
    // device permanently uncallable, since the system drops its socket within
    // seconds of backgrounding.
    const rtStatus = userStatuses[userId];
    console.log('[CALL] placing call to', userId, 'rtStatus:', rtStatus || 'unknown');
    // Request capabilities from server
    const handleCaps = (msg) => {
      if (msg.targetUserId !== userId) return;
      unsub();
      clearTimeout(tid);
      console.log('[E2EE-DIAG] Capabilities for user', userId, ':', msg.capabilities, 'clientType:', msg.clientType);
      if (!msg.capabilities || msg.capabilities.length === 0) {
        // Asleep, not absent. Capabilities are derived from live sockets, so a
        // peer that has to be woken has none to report — and cannot be asked
        // which protocol it prefers. The tunnel is the answer that works
        // without a live WebView or Asterisk, including on a locked screen.
        console.log('[CALL] peer reports no capabilities — asleep; waking via E2EE tunnel');
        startE2eeCall(ext, userId);
        return;
      }
      // One decision, taken here, and carried to the callee in the invite.
      // Deciding independently on each side is what rang SIP at one end while
      // the other waited on the tunnel.
      const wants = callProtocol;                        // 'e2ee' | 'sip' | 'ask'
      const canE2ee = msg.capabilities.includes('e2ee');
      const canSip = msg.capabilities.includes('sip');

      if (wants === 'ask') {
        if (canE2ee && canSip) {
          console.log('[CALL] preference=ask and both offered — asking');
          setCallPicker({ extension: ext, userId, capabilities: msg.capabilities, clientType: msg.clientType });
          return;
        }
        // Asked to choose, and there is nothing to choose between. Deciding
        // silently is how a dead Asterisk looked like a missing feature for
        // days: the setting said "ask", the app never asked, and nothing said
        // why.
        console.log('[CALL] preference=ask but only', msg.capabilities.join(','),
          'available — no choice to offer (clientType:', msg.clientType, ')');
        // Say WHY there is nothing to choose from. "Обычные звонки недоступны"
        // on its own reads as a broken feature; "их телефон спит" reads as a
        // fact about the world, and it is the true one: a sleeping phone is
        // woken by a push into the tunnel, and SIP cannot wake anything.
        setNoChoiceNotice(msg.clientType === 'asleep'
          ? 'Телефон собеседника спит — будим его шифрованным звонком (обычный разбудить не может)'
          : canE2ee
            ? 'Обычные звонки сейчас недоступны — звоним через шифрованный туннель'
            : 'Шифрованный звонок сейчас недоступен — звоним обычным способом');
        setTimeout(() => setNoChoiceNotice(''), 6000);
      }
      // Preference first, then whatever the peer can actually do. E2EE wins
      // ties: it is the only path that keeps the server out of the media.
      const chosen = (wants === 'sip' && canSip) ? 'sip' : (canE2ee ? 'e2ee' : 'sip');
      console.log('[CALL] protocol:', chosen, '(preference:', wants, ', peer offers:', msg.capabilities.join(','), ')');
      if (chosen === 'sip') setActiveCall({ extension: ext });
      else startE2eeCall(ext, userId);
    };
    const unsub = wsService.on('user_capabilities', handleCaps);
    wsService.send({ type: 'get_user_capabilities', targetUserId: userId });
    // Timeout: if no response in 3s, try SIP directly as fallback
    // BUT: don't overwrite an E2EE call that was already set up
    const tid = setTimeout(() => {
      unsub();
      // Fall back to the tunnel, never to SIP. A timeout is not consent to be
      // overheard: SIP puts the conversation through Asterisk, which decrypts
      // it, and doing that silently because a status reply was slow is a
      // privacy downgrade nobody asked for or would notice.
      //
      // The tunnel is also the answer that actually works here — it needs no
      // live peer, since the server wakes a sleeping one by push.
      console.log('[CALL] capabilities timeout — using the tunnel (never a silent SIP downgrade)');
      setActiveCall(prev => {
        if (prev && prev.callProtocol === 'e2ee') return prev;
        return prev;    // do not start a SIP call behind the user's back
      });
      startE2eeCall(ext, userId);
    }, 3000);
  };

  /**
   * Give up on an outgoing call that was never answered.
   *
   * Closing the window is not enough: the callee's phone is ringing on the
   * strength of a server record, and only the server can take it down. An
   * accepted call is a different thing — that is a hangup, and it leaves
   * through the tunnel.
   */
  const cancelOutgoingCall = (call) => {
    const callId = call?.callId || call?.directCallId;
    if (!callId || call.callProtocol !== 'e2ee' || call.accepted) return;
    // A call whose peer joined the tunnel was answered, whatever the accepted
    // flag says: direct_call_accepted can be missed when the callee answers
    // natively. Without this, closing a finished call sent a cancel for it —
    // the server ignored it as "cancel after accept", so nothing broke, but
    // the log said we had abandoned a call we had just held.
    if (call.peerJoined) return;
    console.log('[CALL] cancelling outgoing call', callId);
    wsService.send({ type: 'direct_call_cancel', callId });
  };

  const startSipCall = (ext) => {
    setCallPicker(null);
    setActiveCall({ extension: ext });
  };

  const startE2eeCall = (ext, userId) => {
    setCallPicker(null);
    const callId = crypto.randomUUID();
    console.log('[E2EE-DIAG] Starting E2EE call to user:', userId, 'ext:', ext, 'callId:', callId);
    // State the protocol explicitly. The server records it, hands it to the
    // callee in the invite and in pendingCalls, and downgrades to e2ee if the
    // callee turns out to be asleep — SIP cannot ring a frozen WebView.
    wsService.send({ type: 'direct_call_invite', targetUserId: userId, callId, protocol: 'e2ee' });
    setActiveCall({ extension: ext, callProtocol: 'e2ee', callId, waitingForRoom: true });
  };

  const joinRoom = (notification) => {
    const adminExt = '9' + String(notification.room).slice(1);
    setActiveCall({ extension: adminExt, roomJoin: true, linkCode: notification.code, roomNumber: notification.room });
    setRoomNotifications(prev => prev.filter(n => n.room !== notification.room));
    if (notification.code) setLinkStatuses(prev => ({ ...prev, [notification.code]: { status: 'in_call' } }));
  };

  const joinLinkRoom = (link) => {
    const adminExt = '9' + String(link.room_number).slice(1);
    setActiveCall({ extension: adminExt, roomJoin: true, linkCode: link.code, roomNumber: link.room_number });
    setLinkStatuses(prev => ({ ...prev, [link.code]: { status: 'in_call' } }));
  };

  const dismissRoomNotification = (room) => { setRoomNotifications(prev => prev.filter(n => n.room !== room)); };
  const dismissLinkGuest = (code, roomNum) => {
    setLinkStatuses(prev => { const n = { ...prev }; delete n[code]; return n; });
    dismissRoomNotification(roomNum);
  };

  const handleOverlayAnswer = () => {
    if (!incomingOverlay) return;
    // Dismiss Android system notification if present (we always show it as backup)
    if (window.VoxioxNative?.dismissIncomingNotification) {
      window.VoxioxNative.dismissIncomingNotification();
    }
    // Double-check: don't accept E2EE call if native tunnel is already active
    // (race condition — overlay may have appeared before _nativeCallActive was set)
    if (incomingOverlay.type === 'direct_e2ee' && window._nativeCallActive) {
      console.log('[E2EE-DIAG] handleOverlayAnswer: native call active, dismissing overlay');
      setIncomingOverlay(null);
      return;
    }
    if (incomingOverlay.type === 'call') setActiveCall({ incoming: true, extension: incomingOverlay.caller });
    else if (incomingOverlay.type === 'room') joinRoom(incomingOverlay.room);
    else if (incomingOverlay.type === 'direct_e2ee') {
      console.log('[E2EE-DIAG] Accepting E2EE call:', incomingOverlay.callId, 'room:', incomingOverlay.roomNumber);
      wsService.send({ type: 'direct_call_accept', callId: incomingOverlay.callId });
      setActiveCall({
        extension: incomingOverlay.caller,
        callProtocol: 'e2ee',
        roomNumber: incomingOverlay.roomNumber,
        directCallId: incomingOverlay.callId,
      });
    }
    setIncomingOverlay(null);
  };
  const handleOverlayDecline = () => {
    if (!incomingOverlay) return;
    // Dismiss Android system notification if present
    if (window.VoxioxNative?.dismissIncomingNotification) {
      window.VoxioxNative.dismissIncomingNotification();
    }
    if (incomingOverlay.type === 'call' && incomingOverlay.session) incomingOverlay.session.terminate();
    if (incomingOverlay.type === 'room' && incomingOverlay.room) dismissRoomNotification(incomingOverlay.room.room);
    if (incomingOverlay.type === 'direct_e2ee') wsService.send({ type: 'direct_call_decline', callId: incomingOverlay.callId });
    setIncomingOverlay(null);
  };
  // handleOverlayMinimize/Expand removed — compact banner is always compact

  // ─── CallKit callbacks (iOS native call screen) ───────────────
  useEffect(() => {
    if (!window.VoxioxNative) return;
    window.VoxioxBridge = window.VoxioxBridge || {};
    window.VoxioxBridge.onCallKitAnswer = () => {
      // CallKit answer for link-based calls (room_join).
      // For direct E2EE calls, native side handles CallKit entirely
      // (NativeWSClient/VoIPPush → NativeCallManager → NativeTunnelClient).
      console.log('[E2EE-DIAG] CallKit answer → overlay answer');
      handleOverlayAnswer();
    };
    window.VoxioxBridge.onCallKitEnd = () => {
      console.log('[E2EE-DIAG] CallKit end, activeCall:', !!activeCallRef.current, 'overlay:', !!incomingOverlay);
      // End active call if in progress (CallRoom exposes _endActiveCall).
      // GUARD: Only end if we DON'T have a foreground E2EE call in tunnel mode —
      // a stale CXEndCallAction from a previous CallKit session could arrive
      // after a new foreground tunnel has started, killing it.
      if (window._endActiveCall && !(activeCallRef.current?.callProtocol === 'e2ee' && activeCallRef.current?.roomNumber)) {
        window._endActiveCall();
      } else if (window._endActiveCall) {
        console.log('[E2EE-DIAG] ⛔ Ignoring stale CallKit end — E2EE tunnel call active');
      }
      handleOverlayDecline();
    };
    return () => {
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onCallKitAnswer;
        delete window.VoxioxBridge.onCallKitEnd;
      }
    };
  }); // eslint-disable-line

  const getTelegramLink = async () => { try { const { link } = await api.getTelegramLinkToken(); setTgLink(link); } catch (e) {} };
  const handleInstall = async () => { if (!installPrompt) return; installPrompt.prompt(); const { outcome } = await installPrompt.userChoice; if (outcome === 'accepted') setIsInstalled(true); setInstallPrompt(null); };

  // ─── Auto-generate credentials ──────────────────────────────
  const generateUsername = () => {
    const bytes = new Uint8Array(3);
    crypto.getRandomValues(bytes);
    return `user-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
  };
  const generatePassword = () => {
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const special = '#$@!%&*?';
    const all = lower + upper + digits + special;
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    const pick = (cs, b) => cs[b % cs.length];
    const chars = [pick(lower, arr[0]), pick(upper, arr[1]), pick(digits, arr[2]), pick(special, arr[3]), ...Array.from(arr.slice(4), b => all[b % all.length])];
    for (let i = chars.length - 1; i > 0; i--) { const j = arr[i % arr.length] % (i + 1); [chars[i], chars[j]] = [chars[j], chars[i]]; }
    return chars.join('');
  };

  const createUser = async () => {
    setAdminError('');
    setCreatedCreds(null);
    try {
      // The server names the account and issues the password: a browser is the
      // wrong place to decide how strong somebody else's credentials are.
      const u = await api.adminCreateUser({});
      setAdminUsers(prev => [u, ...prev]);
      setCreatedCreds({ username: u.username, password: u.password, sip_extension: u.sip_extension });
    } catch (e) { setAdminError(e.message); }
  };
  /**
   * Deleting a user is the most destructive thing in the app, and it used to
   * be a window.confirm behind an 18-pixel button inside a scrolling list.
   * The sheet names what goes with the account; the row no longer offers the
   * action at all (docs/plans/settings-and-admin.md, этап 0).
   */
  const [settingsError, setSettingsError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteUser = (user) => setPendingDelete(user);
  const confirmDeleteUser = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.adminDeleteUser(pendingDelete.id);
      setAdminUsers(prev => prev.filter(u => u.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      setAdminError(e.message);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const buildShareMessage = (creds) => [
    '\u{1F512} Voxiox — Защищённые звонки', '',
    `\u{1F4F1} Войти: ${publicOrigin()}`,
    `\u{1F464} Логин: ${creds.username}`,
    `\u{1F511} Пароль: ${creds.password}`, '',
    'По умолчанию включено E2E шифрование.',
    'В настройках доступны разные методы шифрования.',
    'Если протокол изменён — потяните экран вниз для обновления.',
  ].join('\n');
  const copyCredentials = async () => {
    if (!createdCreds) return;
    try { await navigator.clipboard.writeText(buildShareMessage(createdCreds)); }
    catch { const ta = document.createElement('textarea'); ta.value = buildShareMessage(createdCreds); ta.style.cssText = 'position:fixed;opacity:0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    setCredsCopied(true); setTimeout(() => setCredsCopied(false), 2000);
  };
  const shareCredentials = () => {
    if (!createdCreds) return;
    if (navigator.share) { navigator.share({ text: buildShareMessage(createdCreds), title: 'Voxiox' }).catch(() => {}); }
    else { copyCredentials(); }
  };

  // ─── Friends (ADR-013) ───────────────────────────────────────
  const searchTimer = useRef(null);
  const doUserSearch = (q) => {
    const query = (q || '').trim();
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query) { setSearchResults([]); setSearchBusy(false); return; }
    setSearchBusy(true);
    searchTimer.current = setTimeout(async () => {
      try { setSearchResults(await api.searchUsers(query)); }
      catch (e) { setSearchResults([]); }
      finally { setSearchBusy(false); }
    }, 300);
  };
  const sendFriendRequest = async (userId) => {
    try {
      const r = await api.sendFriendRequest(userId);
      // Reflect the new state in the current search results / friend list.
      setSearchResults(prev => prev.map(u => u.id === userId
        ? { ...u, relationship: r.status === 'friends' ? 'friends' : 'requested' } : u));
      if (r.status === 'friends') { loadUsers(); loadFriendRequests(); }
    } catch (e) {}
  };
  const acceptRequest = async (requesterId) => {
    try {
      await api.acceptFriendRequest(requesterId);
      setIncomingRequests(prev => prev.filter(rq => rq.user_id !== requesterId));
      setSearchResults(prev => prev.map(u => u.id === requesterId ? { ...u, relationship: 'friends' } : u));
      loadUsers();
    } catch (e) {}
  };
  const declineRequest = async (requesterId) => {
    try {
      await api.declineFriendRequest(requesterId);
      setIncomingRequests(prev => prev.filter(rq => rq.user_id !== requesterId));
    } catch (e) {}
  };
  const removeFriend = async (userId) => {
    try {
      await api.removeFriend(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      loadFriendRequests();
    } catch (e) { setLoadError(e.message || 'Не удалось удалить контакт'); setTimeout(() => setLoadError(''), 5000); }
  };
  const blockUser = async (userId) => {
    try {
      await api.blockUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      loadFriendRequests();
      loadBlocked();
    } catch (e) { setLoadError(e.message || 'Не удалось заблокировать'); setTimeout(() => setLoadError(''), 5000); }
  };
  const unblockUser = async (userId) => {
    try {
      await api.unblockUser(userId);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (e) { setLoadError(e.message || 'Не удалось разблокировать'); setTimeout(() => setLoadError(''), 5000); }
  };
  // Cancelling an outgoing request is the same server-side operation as
  // removing a friend: DELETE /friends/:id drops the edge either way.
  const cancelRequest = (userId) => removeFriend(userId);

  // Devices of a known person whose identity key we have never seen. Sending
  // is blocked while this is non-empty for that person: encrypting to a key
  // nobody vouched for is how a conversation reaches somebody else's device
  // with nothing on screen to show it (K1).
  const [untrustedDevices, setUntrustedDevices] = useState({});
  const verifyBundleRef = useRef(null);
  if (!verifyBundleRef.current) {
    verifyBundleRef.current = makeBundleVerifier(tofu, {
      deviceList: deviceGateRef.current,
      onRefused: (d) => {
        console.warn('[CHAT] refusing to build a session with an unseen device',
          d.deviceId, 'of user', d.userId);
        setUntrustedDevices(prev => {
          const list = prev[d.userId] || [];
          if (list.some(x => x.deviceId === d.deviceId)) return prev;
          return { ...prev, [d.userId]: [...list, d] };
        });
      },
    });
  }

  /**
   * The person says: he lost every device and started his account over.
   *
   * The pinned account key is what makes such a change mean something, so it
   * is dropped only here, deliberately, after a warning — never as a side
   * effect of some other button. Afterwards this account is at first sight
   * again: the next list is pinned as a stranger's would be, sessions and
   * message keys are untouched, and the server's key log still carries the
   * master-change event for anyone comparing notes.
   */
  const acceptNewAccountKey = useCallback((peerUserId) => {
    forgetAccountKey(messageStore, peerUserId)
      .then(() => {
        console.warn('[CHAT] account key of user', peerUserId, 'forgotten by the owner — the next list is pinned afresh');
        forgetImpostorNotice(peerUserId);
        setUntrustedDevices(prev => { const n = { ...prev }; delete n[peerUserId]; return n; });
        return jobsRef.current.release(`chat:${peerUserId}`);
      })
      .then((n) => (n ? jobsRef.current.runOnce() : null))
      .catch((e) => console.warn('[CHAT] could not forget the account key:', e?.message));
  }, []);

  /**
   * Drop a dead session and make the other side rebuild it.
   *
   * The empty message is the point: with no session left, encryptFor has to
   * start one from their published bundle, so what goes out is a handshake.
   * The peer adopts it, and their next message opens. Signal does exactly
   * this in AutomaticSessionResetJob.
   *
   * It was written on 30.08, deleted by an unrelated refactor on 31.08 and
   * left as a call to a name that no longer existed — «Can't find variable:
   * rebuildSessionRef» on the one path nobody exercises until a session
   * really breaks. Which it did: an iPhone spent an afternoon showing «не
   * расшифровано» while the recovery it was asking for threw before it
   * started (03.09.2026).
   */
  const rebuildSession = useCallback(async (peerUserId, deviceId) => {
    const sessions = sessionsRef.current;
    if (!sessions) return;
    try {
      await sessions.resetSession(peerUserId, deviceId);
      console.log('[CHAT] dropped the session with user', peerUserId,
        'device', String(deviceId).slice(0, 8), '— sending a handshake');
      const { send, blocked } = await encryptMessage(sessions, {
        verifyBundle: verifyBundleRef.current,
        toUserId: peerUserId,
        clientMid: (crypto.randomUUID?.() || `rekey-${Date.now()}`),
        msgType: 'rekey',
        content: '',
      });
      if (blocked && blocked.length) {
        console.warn('[CHAT] cannot rebuild: the peer has a device nobody has confirmed');
        return;
      }
      for (const payload of send) wsService.send(payload);
      console.log('[CHAT] handshake sent to', send.length, 'device(s)');
    } catch (e) {
      console.warn('[CHAT] could not rebuild the session:', e?.message);
    }
  }, []);
  const rebuildSessionRef = useRef(rebuildSession);
  rebuildSessionRef.current = rebuildSession;

  /** The person said the new device is theirs: pin it and let sending resume. */
  const trustPeerDevice = useCallback((peerUserId, device) => {
    trustDevice(tofu, peerUserId, device.identitySig, device.deviceLabel);
    console.log('[CHAT] device', device.deviceId, 'of user', peerUserId, 'confirmed by the owner');
    // Whatever was held back for this person goes now. Confirming IS the event
    // the held job was waiting for — it does not poll for it, because every
    // attempt would spend one of the unconfirmed device's one-time prekeys to
    // re-ask a question only this person can answer.
    jobsRef.current.release(`chat:${peerUserId}`)
      .then((n) => (n ? jobsRef.current.runOnce() : null))
      .then((r) => { if (r?.ran) console.log('[CHAT] released', r.ran, 'held message(s)'); })
      .catch(() => {});
    setUntrustedDevices(prev => {
      const list = (prev[peerUserId] || []).filter(x => x.deviceId !== device.deviceId);
      const next = { ...prev };
      if (list.length) next[peerUserId] = list; else delete next[peerUserId];
      return next;
    });
  }, []);

  // The system went back past our entry: whatever it was showing must close.
  // Without this the chat would stay on screen with no history left under it,
  // and the next Back would quit the app.
  useEffect(() => {
    // Only the chat closes on an empty stack. Settings screens live on the
    // same stack now, and the top entry says which of them is showing.
    const onChat = (nav.top?.name || '').startsWith('chat:');
    if (!onChat && chatUser) setChatUser(null);
  }, [nav.depth, nav.top]); // eslint-disable-line

  /**
   * An address in the bar opens the screen it names.
   *
   * The point of putting settings on the stack: #settings/calls/route is a
   * place somebody can be sent to — from a banner about a failed connection,
   * or from a message.
   */
  useEffect(() => {
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (hash.startsWith('settings/')) {
      setTab('settings');
      nav.push(hash);
    }
  }, []); // eslint-disable-line

  /**
   * Start the queue once the socket is up, and keep it running.
   *
   * Everything outgoing goes through it, so this is also what drains whatever
   * was written down while the app was closed.
   */
  useEffect(() => {
    const q = jobsRef.current;
    q.start();
    return () => q.stop();
  }, []);

  const openChat = async (u) => {
    setChatUser(u);
    // An entry in the browser history, which is what hardware Back reads.
    nav.push(`chat:${u.id}`);
    const conv = conversationId(user.id, u.id);

    // Render from cache first: on a phone that has been here before this is
    // instant and costs no traffic, and the network round-trip below only fills
    // in what actually changed.
    let cached = [];
    // Bounded: a storage layer that never answers must not hold the chat empty.
    cached = await withTimeout(messageStore.getMessages(conv), CACHE_TIMEOUT_MS, []);
    if (cached.length) setChatMessages(cached);
    const meta = await withTimeout(messageStore.getMeta(conv), CACHE_TIMEOUT_MS, {});
    setChatAnchorId(meta.lastReadId || 0);
    setChatHasMore(true);

    try {
      const since = lastId(cached);
      const { messages, hasMore } = since
        ? await fetchMessages(u.id, { after: since })
        : await fetchMessages(u.id);
      // Show what the server sent even if the cache write is slow or stuck.
      const merged = await withTimeout(
        since ? messageStore.putMessages(conv, messages) : messageStore.replaceMessages(conv, messages),
        CACHE_TIMEOUT_MS,
        since ? [...cached, ...messages] : messages,
      );
      setChatMessages(merged);
      if (!since) setChatHasMore(hasMore !== false);
      const newest = lastId(merged);
      if (newest) messageStore.setMeta(conv, { lastReadId: newest }).catch(() => {});
    } catch (e) {
      // The cached conversation stays on screen instead of a blank one — but
      // say what happened. Silence here hid a 500 from the history endpoint
      // for a whole session: on a phone with no cache the chat opened empty
      // and looked like lost messages rather than a failed request.
      console.warn('[CHAT] history load failed for user', u.id, '—', e?.status || '', e?.message || e);
    }
    // Messages still waiting for a socket belong on screen, marked as such —
    // they are real, they are just not delivered. Without this they vanished
    // at the first restart and looked like the app had eaten them.
    try {
      // Both kinds: frames waiting for a socket, and plaintext waiting for a
      // person to confirm a device. The second kind must keep its own reason —
      // shown as "waiting" it would look like a network hiccup, and the person
      // would never press the button that actually releases it.
      const queued = [
        ...(await jobsRef.current.pendingOfType('ws_send')),
        ...(await jobsRef.current.pendingOfType('chat_send')),
      ].filter(j => j.payload?.row && j.payload.toUserId === u.id);
      if (queued.length) {
        setChatMessages(prev => {
          const have = new Set(prev.map(m => m.clientMid).filter(Boolean));
          const extra = queued.map(j => j.payload.row)
            .filter(r => r && !have.has(r.clientMid))
            .map(r => (r.sendError ? r : { ...r, _pending: true }));
          return extra.length ? [...prev, ...extra] : prev;
        });
      }
    } catch (e) { /* the conversation is more important than the queue */ }

    jobsRef.current.runOnce().catch(() => {});

    // The person opened the chat, so it is read — whether or not the network
    // agreed to hand over the page, and whether or not the socket is up right
    // now. Hanging the receipt on either left messages unread on the server
    // after they had been looked at.
    jobsRef.current.enqueue('ws_send', { frames: [{ type: 'chat_read', from: u.id }] },
      { queue: `chat:${u.id}`, dedupeKey: `read:${u.id}` })
      .then(() => jobsRef.current.runOnce())
      .catch(() => {});
    setUnreadCounts(prev => ({ ...prev, [u.id]: 0 }));
  };
  openChatRef.current = openChat;

  /** Older page, fetched when the user scrolls into history. */
  const loadOlderMessages = useCallback(async () => {
    const u = chatUserRef.current;
    if (!u || loadingOlderRef.current) return;
    const oldest = firstId(chatMessagesRef.current);
    if (!oldest) return;
    loadingOlderRef.current = true;
    try {
      const { messages, hasMore } = await fetchMessages(u.id, { before: oldest });
      if (messages.length) {
        const conv = conversationId(user.id, u.id);
        const merged = await withTimeout(
          messageStore.putMessages(conv, messages),
          CACHE_TIMEOUT_MS,
          [...messages, ...chatMessagesRef.current],
        );
        setChatMessages(merged);
      }
      setChatHasMore(hasMore !== false && messages.length > 0);
    } catch (e) {
      setChatHasMore(false);
    } finally {
      loadingOlderRef.current = false;
    }
  }, [user]);
  /**
   * History from the server arrives as ciphertext. Open it here, once, so no
   * screen ever has to know — and pass this device's id, because the server
   * stores one copy per device and hands back only ours.
   */
  const openRows = useCallback(async (rows) => {
    const intake = intakeRef.current;
    if (!intake || !Array.isArray(rows)) return rows || [];
    const out = await intake.acceptAll(rows);
    return out.filter(r => !r.skip);
  }, []);

  const fetchMessages = useCallback(async (peerId, { after = 0, before = 0 } = {}) => {
    const dev = sessionsRef.current?.deviceId || null;
    const r = after ? await api.getMessagesAfter(peerId, after, dev)
      : before ? await api.getMessagesBefore(peerId, before, dev)
      : await api.getMessages(peerId, 1, dev);
    return { ...r, messages: await openRows(r.messages) };
  }, [openRows]);

  const sendMessage = (overrideContent, msgType, fileRef, replyToId) => {
    if (!chatUser) return;
    const content = overrideContent ?? chatInput.trim();
    // A file arrives as the envelope-ready meta from api.uploadSealed
    // ({id, k, name, mime, size, dur}); a bare id still works and means a
    // pre-encryption file with nothing to carry but the id.
    const fileMeta = fileRef ? (typeof fileRef === 'object' ? fileRef : { id: fileRef }) : null;
    const fileId = fileMeta ? fileMeta.id : null;
    if (!content && !fileId) return;

    // Find reply-to message for optimistic preview
    let replyMeta = {};
    if (replyToId) {
      const replyMsg = chatMessages.find(m => m.id === replyToId);
      if (replyMsg) {
        replyMeta = {
          reply_to_id: replyToId,
          reply_content: (replyMsg.content || '').slice(0, 100),
          reply_msg_type: replyMsg.msgType || replyMsg.type || 'text',
          reply_from_id: replyMsg.from_user_id,
          reply_from_name: replyMsg.from_user_id === user.id ? user.username : chatUser.username,
        };
      }
    }

    // Optimistic: show message immediately (fixes bug where chat_sent echo is lost).
    // clientMid is chosen here and travels with the message: it reconciles the
    // echo, and it makes a resend after a dropped socket idempotent server-side
    // instead of creating a second row (ADR-011 phase 0).
    const clientMid = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const tempMsg = {
      clientMid,
      id: `temp_${clientMid}`,
      from_user_id: user.id,
      to_user_id: chatUser.id,
      content: content || '',
      msgType: msgType || 'text',
      file_id: fileId || null,
      // The optimistic bubble renders from the same fields the archive will
      // hold — including the key, or a fresh sealed image would not open on
      // the sender's own screen.
      ...(fileMeta && fileMeta.k ? {
        file_key: fileMeta.k, file_name: fileMeta.name, file_mime: fileMeta.mime,
        file_size: fileMeta.size, file_duration: fileMeta.dur,
      } : {}),
      ...replyMeta,
      created_at: new Date().toISOString(),
      read: 0,
      _pending: true,
    };
    setChatMessages(prev => [...prev, tempMsg]);
    if (!overrideContent && !fileId) setChatInput('');

    // Built before the work starts, so the failure path can write the
    // message down with its reply intact (a const inside the try was out of
    // scope in the catch: «Can't find variable: replyPayload», 2026-09-02).
    const replyPayload = replyToId ? {
      id: replyToId,
      t: replyMeta.reply_msg_type || 'text',
      fromId: replyMeta.reply_from_id,
      preview: replyMeta.reply_content || '',
    } : null;
    (async () => {
      try {
        // Wait for the ratchet rather than refusing on the spot. Sending in
        // the clear "just this once" is how a private messenger stops being
        // one, so the wait has an end: if the device cannot bring encryption
        // up at all, the message is marked unsent and stays visible.
        let sessions = sessionsRef.current;
        if (!sessions) {
          console.log('[CHAT] ratchet still starting — holding the message');
          sessions = await Promise.race([
            sessionsReadyRef.current.promise,
            new Promise((r) => setTimeout(() => r(null), 15000)),
          ]);
        }
        if (!sessions) {
          console.error('[CHAT] no ratchet after waiting — refusing to send unencrypted');
          setChatMessages(prev => prev.map(m =>
            m.clientMid === clientMid ? { ...m, _pending: false, sendError: 'E_NO_SESSION' } : m));
          return;
        }
        const { send, failed, blocked } = await encryptMessage(sessions, {
          verifyBundle: verifyBundleRef.current,
          toUserId: chatUser.id,
          clientMid,
          msgType: msgType || 'text',
          content: content || '',
          fileMeta,
          reply: replyPayload,
          gossip: keyLogGossipRef.current,
        });
        if (blocked && blocked.length) {
          // Nothing went out. Encrypting to the devices we do trust and
          // skipping the rest would look like a delivered message while an
          // unverified key sits in the conversation.
          //
          // The message is WRITTEN DOWN before the screen is told about it.
          // Marking the bubble was all this did, and the bubble lived in React
          // state alone: going to confirm the key — the one thing the banner
          // asks for — rebuilt the conversation from the archive, which had
          // never heard of this message, and it was gone. It was not deleted;
          // it was never anywhere. The job holds the plaintext until the
          // person confirms, and goes out by itself the moment they do.
          console.warn('[CHAT] send blocked —', blocked.length, 'device(s) not confirmed');
          const heldRow = { ...tempMsg, _pending: false, sendError: 'E_UNTRUSTED' };
          await jobsRef.current.enqueue('chat_send', {
            row: heldRow,
            toUserId: chatUser.id,
            clientMid,
            msgType: msgType || 'text',
            content: content || '',
            fileMeta,
            reply: replyPayload,
            fileId: fileId || null,
            replyToId: replyToId || null,
          }, { queue: `chat:${chatUser.id}`, dedupeKey: `msg:${clientMid}` });
          setChatMessages(prev => prev.map(m => (m.clientMid === clientMid ? heldRow : m)));
          return;
        }
        if (!send.length) throw new Error('нет ни одного устройства для доставки');
        // Written down BEFORE it is handed to the socket. wsService.send drops
        // whatever it cannot deliver, so without this a message composed with
        // no signal existed on screen and nowhere else, and the next restart
        // erased it. Re-sending later is safe: the server collapses a repeat
        // onto the same row by client_mid.
        for (const payload of send) {
          if (fileId) payload.fileId = fileId;
          if (replyToId) payload.replyToId = replyToId;
        }
        // Written down before the socket is offered it. The job carries the
        // bubble too, so an unsent message can be shown as itself after a
        // restart rather than vanishing.
        await jobsRef.current.enqueue('ws_send', { frames: send, row: tempMsg, toUserId: chatUser.id },
          { queue: `chat:${chatUser.id}`, dedupeKey: `msg:${clientMid}` });
        jobsRef.current.runOnce().catch(() => {});
        if (failed.length) {
          console.warn('[CHAT] delivered to', send.length, 'devices,', failed.length, 'could not be reached:', failed);
        }
        // Without a copy for this device the message is on screen and nowhere
        // else: there is no row to cache and nothing to fetch back later, so
        // it disappears at the next open. Worth a line, because the symptom
        // appears minutes later and looks like the server lost it.
        if (!send.some(p => p.targetDeviceId === sessions.deviceId)) {
          console.warn('[CHAT] no copy for this device — sent message will not survive a reload');
        }
      } catch (e) {
        // A failure to encrypt is not a lost message. This used to mark the
        // bubble E_ENCRYPT and stop: the bubble lived in React state alone, so
        // a 429 on the bundle fetch (two phones behind one address, 02.09.2026)
        // left the person a greyed-out line that the next relaunch erased —
        // never sent, never written anywhere. The job carries the message and
        // retries with backoff, the way the held path already did; the bubble
        // says it is retrying and is matched to its echo when it goes out.
        console.error('[CHAT] encrypt failed:', e);
        const retryRow = { ...tempMsg, _pending: false, sendError: 'E_RETRY', sendErrorDetail: e?.message || String(e) };
        try {
          await jobsRef.current.enqueue('chat_send', {
            row: retryRow,
            toUserId: chatUser.id,
            clientMid,
            msgType: msgType || 'text',
            content: content || '',
            fileMeta,
            reply: replyPayload,
            fileId: fileId || null,
            replyToId: replyToId || null,
          }, { queue: `chat:${chatUser.id}`, dedupeKey: `msg:${clientMid}` });
          setChatMessages(prev => prev.map(m => (m.clientMid === clientMid ? retryRow : m)));
        } catch (e2) {
          console.error('[CHAT] could not write the message down for retry:', e2);
          setChatMessages(prev => prev.map(m =>
            m.clientMid === clientMid ? { ...m, _pending: false, sendError: 'E_ENCRYPT' } : m));
        }
      }
    })();
  };

  const registerWebAuthn = async () => {
    try { const { startRegistration } = await import('@simplewebauthn/browser'); const options = await api.webauthnRegisterOptions(); const credential = await startRegistration(options); await api.webauthnRegister(credential); alert('Face ID / биометрия добавлена!'); } catch (err) { alert(err.message || 'Не удалось зарегистрировать ключ'); }
  };

  // ─── Helpers ─────────────────────────────────────────────────
  // Parse server timestamps as UTC (SQLite CURRENT_TIMESTAMP omits 'Z' suffix)
  // Time formatting moved to services/time.mjs: it used to live here and be
  // threaded to three components as a prop, so anything not handed the prop
  // parsed SQLite's zone-less timestamps as local and showed the wrong hour.
  const totalUnread = Object.values(unreadCounts).reduce((s, c) => s + c, 0);

  const groupByDate = (calls) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    calls.forEach(call => {
      const d = utc(call.created_at).toDateString();
      const label = d === today ? 'Сегодня' : d === yesterday ? 'Вчера' : utc(call.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(call);
    });
    return Object.entries(groups);
  };

  // ═══════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div style={S.container} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {/* Overlays */}
      {activeCall && !activeCall.waitingForRoom && (
        <>
          {callMinimized && (
            <div style={activeCallBar.bar} onClick={() => setCallMinimized(false)}>
              <div style={activeCallBar.dot} />
              <span style={activeCallBar.text}>Нажмите, чтобы вернуться к звонку</span>
              <span style={activeCallBar.chevron}>›</span>
            </div>
          )}
          <CallRoom linkCode={activeCall.linkCode} targetExtension={activeCall.extension} incoming={activeCall.incoming} roomJoin={activeCall.roomJoin} iceServers={iceServers} iceTransportPolicy={privacyMode >= 1 ? 'relay' : 'all'} tunnelMode={activeCall.callProtocol === 'e2ee' || (privacyMode >= 4 && activeCall.roomJoin)} roomNumber={activeCall.roomNumber} directCallId={activeCall.directCallId} nativeDisplayMode={activeCall.nativeDisplayMode} nativeSAS={activeCall.nativeSAS} nativeElapsed={activeCall.nativeElapsed} nativeMuted={activeCall.nativeMuted} minimized={callMinimized} onMinimize={() => setCallMinimized(true)}
            onPeerJoined={() => setActiveCall(prev => prev ? { ...prev, peerJoined: true } : prev)} onClose={() => { cancelOutgoingCall(activeCall); if (activeCall?.linkCode) { setLinkStatuses(prev => { const n = { ...prev }; delete n[activeCall.linkCode]; return n; }); api.clearActiveRoom(activeCall.linkCode).catch(() => {}); } setActiveCall(null); setCallMinimized(false); }} />
        </>
      )}
      {incomingOverlay && <CompactCallBanner type={incomingOverlay.type} caller={incomingOverlay.caller} label={incomingOverlay.label} onAnswer={handleOverlayAnswer} onDecline={handleOverlayDecline} />}

      {/* ─── Call protocol picker ─────────────────────── */}
      {callPicker && (
        <CallProtocolPicker
          capabilities={callPicker.capabilities}
          onSip={() => startSipCall(callPicker.extension)}
          onE2ee={() => startE2eeCall(callPicker.extension, callPicker.userId)}
          onClose={() => setCallPicker(null)}
        />
      )}

      {noChoiceNotice && (
        <div style={{
          position: 'fixed', left: 16, right: 16, bottom: 96, zIndex: 9999,
          background: colors.gray100, border: '1px solid ' + colors.gray200,
          borderRadius: 12, padding: '12px 16px', fontSize: 14, color: colors.gray700,
          textAlign: 'center',
        }}>{noChoiceNotice}</div>
      )}

      {/* ─── Waiting for E2EE room assignment ──────────── */}
      {activeCall?.waitingForRoom && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
          <div style={{ background: colors.white, borderRadius: 16, padding: '32px 24px', textAlign: 'center' }}>
            <div style={{ marginBottom: 12, fontSize: 15, color: colors.gray500 }}>Подключение...</div>
            <button onClick={() => { cancelOutgoingCall(activeCall); setActiveCall(null); }} style={{ padding: '8px 20px', border: '1px solid ' + colors.gray200, borderRadius: 10, background: colors.gray100, cursor: 'pointer', fontSize: 14, color: colors.gray500 }}>Отмена</button>
          </div>
        </div>
      )}

      {/* On native this shows for the moment before the automatic reload, and
          for as long as a call keeps that reload waiting. */}
      {updateAvailable && (
        <div style={S.updateBanner} onClick={() => window.location.reload()}>
          {isNative ? 'Обновление — приложение перезагрузится' : 'Доступно обновление — нажмите для обновления'}
        </div>
      )}

      {/* ─── Pull-to-refresh indicator ─────────────────────── */}
      {pullY > 0 && (
        <div style={{ ...S.pullIndicator, transform: `translateY(${pullY - 40}px)`, opacity: Math.min(pullY / 60, 1) }}>
          <div style={{ ...S.pullSpinner, animation: refreshing ? 'spin 0.8s linear infinite' : 'none', transform: refreshing ? 'none' : `rotate(${pullY * 4}deg)` }}>
            {refreshing ? <RefreshCw size={18} color={colors.gray900} /> : <ArrowDown size={18} color={colors.gray900} />}
          </div>
        </div>
      )}

      {/* ─── Header ──────────────────────────────────────────── */}
      <header style={{ ...S.header, ...(callMinimized ? { paddingTop: 'calc(max(16px, env(safe-area-inset-top)) + 36px)' } : {}) }}>
        <div style={S.headerTop}>
          <SignalBars sipConnected={sipConnected} wsConnected={privacyLoaded} privacyMode={privacyMode} />
          <span style={S.headerUser}>{displayName(user)}</span>
        </div>
        <div style={S.headerTitle}>{TAB_LABELS[tab] || 'Voxiox'}</div>
      </header>

      {/* Room notifications now shown inline in link cards */}

      {/* ─── Main Content ────────────────────────────────────── */}
      <main ref={scrollContainerRef} style={S.main}>

        {/* ═══ CALLS TAB ═══ */}
        {tab === 'calls' && (
          <CallsTab
            links={links} linkStatuses={linkStatuses} loadError={loadError} copied={copied}
            showDialpad={showDialpad} setShowDialpad={setShowDialpad}
            showCreateSheet={showCreateSheet} setShowCreateSheet={setShowCreateSheet}
            newLabel={newLabel} setNewLabel={setNewLabel} maxParticipants={maxParticipants} setMaxParticipants={setMaxParticipants}
            ttlHours={ttlHours} setTtlHours={setTtlHours} loading={loading}
            createLink={createLink} copyLink={copyLink} deleteLink={deleteLink}
            joinLinkRoom={joinLinkRoom} dismissLinkGuest={dismissLinkGuest}
            qrUrl={qrUrl} setQrUrl={setQrUrl}
            callUser={callUser} getLinkUrl={getLinkUrl} fmtDate={fmtDate} utc={utc}
          />
        )}

        {/* ═══ HISTORY TAB ═══ */}
        {tab === 'history' && (
          <HistoryTab history={history} user={user} callUser={callUser} fmtDate={fmtDate} fmtDur={fmtDur} groupByDate={groupByDate} />
        )}

        {/* ═══ CONTACTS TAB ═══ */}
        {tab === 'users' && (
          <ContactsTab
            users={users} user={user} userStatuses={userStatuses}
            contactSearch={contactSearch} setContactSearch={setContactSearch}
            searchResults={searchResults} searchBusy={searchBusy} onSearch={doUserSearch} sendFriendRequest={sendFriendRequest}
            incomingRequests={incomingRequests} acceptRequest={acceptRequest} declineRequest={declineRequest}
            outgoingRequests={outgoingRequests} cancelRequest={cancelRequest}
            blockedUsers={blockedUsers} unblockUser={unblockUser}
            removeFriend={removeFriend} blockUser={blockUser}
            openChat={openChat} callUser={callUser} setTab={setTab}
            formatLastSeen={formatLastSeen} utc={utc}
          />
        )}

        {/* ═══ CHAT TAB ═══ */}
        {tab === 'chat' && !chatUser && (
          <ChatListTab users={users} lastMessages={lastMessages} user={user} unreadCounts={unreadCounts} openChat={openChat} fmtTime={fmtTime} utc={utc} userStatuses={userStatuses} />
        )}

        {/* ═══ MORE TAB ═══ */}
        {tab === 'more' && (
          <SettingsTab
            nav={nav}
            user={user} logout={logout}
            onDisplayNameSaved={(name) => setUser(prev => prev ? { ...prev, display_name: name } : prev)}
            privacyMode={privacyMode} setPrivacyMode={setPrivacyMode} privacyLoaded={privacyLoaded}
            callProtocol={callProtocol}
            onCallProtocolChange={(p) => {
              // The choice is shown immediately and taken back if the server
              // refuses it. `catch(() => {})` drew a tick next to a setting
              // that had not been saved, and the next call went out over the
              // protocol the person thought they had turned off.
              const previous = callProtocol;
              setCallProtocol(p);
              setSettingsError('');
              api.updateSettings({ call_protocol: p })
                .then(() => console.log('[CALL] preference set to', p))
                .catch((e) => {
                  setCallProtocol(previous);
                  setSettingsError('Не удалось сохранить — выбор остался прежним');
                  console.warn('[CALL] preference NOT saved:', e?.message);
                  setTimeout(() => setSettingsError(''), 6000);
                });
            }}
            settingsError={settingsError}
            isNative={isNative} nativeFaceIdLinked={nativeFaceIdLinked} linkNativeFaceId={linkNativeFaceId} unlinkNativeFaceId={unlinkNativeFaceId}
            keepAlive={keepAlive} setKeepAlive={setKeepAlive}
            tgLink={tgLink} getTelegramLink={getTelegramLink}
            isInstalled={isInstalled} installPrompt={installPrompt} handleInstall={handleInstall}
            showShareContact={showShareContact} setShowShareContact={setShowShareContact}
            registerWebAuthn={registerWebAuthn}
            adminUsers={adminUsers} adminError={adminError} createdCreds={createdCreds} credsCopied={credsCopied}
            createUser={createUser} deleteUser={deleteUser} copyCredentials={copyCredentials} shareCredentials={shareCredentials}
            untrustedCount={Object.values(untrustedDevices).reduce((n, list) => n + list.length, 0)}
            keyLogAlarm={keyLogAlarm}
            impostorNotices={impostorNotices}
            acceptNewAccountKey={acceptNewAccountKey}
            chatDeviceId={chatDeviceId}
            accountDeviceState={accountDeviceState}
            callActive={!!activeCall}
            setCreatedCreds={setCreatedCreds} setCredsCopied={setCredsCopied}
            copyLink={copyLink}
            APP_VERSION={APP_VERSION} BUILD_DATE={BUILD_DATE}
            onPrivacyChange={(mode) => {
              const prev = privacyMode;
              setPrivacyMode(mode);
              api.updateSettings({ privacy_mode: mode }).catch(() => {});
              if (mode < 4) {
                api.sipCreds().then(sipData => {
                  const servers = [{ urls: sipData.stun || `stun:${serverHostname()}:3478` }];
                  if (sipData.turn) servers.push({ urls: sipData.turn.urls, username: sipData.turn.username, credential: sipData.turn.credential });
                  setIceServers(servers);
                }).catch(() => {});
              }
              if ((prev >= 3) !== (mode >= 3)) {
                const token = localStorage.getItem('voxiox_token');
                if (token) { wsService.disconnect(); setTimeout(() => wsService.connect(token, mode), 300); }
              }
            }}
          />
        )}
      </main>

      {/* ─── Full-screen Chat Overlay ─────────────────────────── */}
      {pendingDelete && (
        <ConfirmSheet
          title={`Удалить ${pendingDelete.display_name || '@' + pendingDelete.username}?`}
          consequences={[
            'Переписка этого человека со всеми собеседниками удаляется без возможности восстановления',
            'Файлы и записи разговоров, которые он присылал, стираются с сервера',
            'История звонков и его ссылки для звонков перестают работать',
            'SIP-номер освобождается, вошедшие устройства теряют доступ',
          ]}
          confirmLabel="Удалить навсегда"
          busy={deleting}
          onConfirm={confirmDeleteUser}
          onClose={() => setPendingDelete(null)}
        />
      )}

      {chatUser && <ChatOverlay
        untrustedDevices={untrustedDevices[chatUser.id] || []}
        onTrustDevice={(d) => trustPeerDevice(chatUser.id, d)}
        onAcceptNewAccountKey={() => {
          if (window.confirm('Принять новый ключ аккаунта собеседника?\n\nТак выглядит человек, потерявший все устройства, — и так же выглядит подмена со стороны сервера. Сверьте отпечаток голосом, прежде чем соглашаться.')) {
            acceptNewAccountKey(chatUser.id);
          }
        }}
        chatUser={chatUser}
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        sendMessage={sendMessage}
        onBack={() => nav.pop() || setChatUser(null)}
        onCall={() => callUser(chatUser.sip_extension, chatUser.id)}
        onLoadOlder={loadOlderMessages}
        hasMoreHistory={chatHasMore}
        anchorMessageId={chatAnchorId}
        userId={user.id}
        userName={displayName(user)}
        messagesEndRef={messagesEndRef}
        fmtTime={fmtTime}
        formatLastSeen={formatLastSeen}
        spamStateRef={spamStateRef}
      />}

      {/* ─── Bottom Tab Bar ──────────────────────────────────── */}
      <nav style={{ ...S.tabBar, ...(chatUser ? { display: 'none' } : {}) }}>
        {TAB_KEYS.map(key => {
          const TabIcon = TAB_ICONS[key];
          const active = tab === key;
          return (
            <button key={key} onClick={() => goToTab(key)} style={{ ...S.tabBtn, ...(active ? S.tabBtnActive : {}) }}>
              <TabIcon size={24} color={active ? colors.gray900 : colors.gray400} strokeWidth={active ? 2.5 : 2} />
              <span style={{ ...S.tabLabel, ...(active ? S.tabLabelActive : {}) }}>{TAB_LABELS[key]}</span>
              {key === 'chat' && totalUnread > 0 && <span style={S.tabBadge}>{totalUnread}</span>}
            </button>
          );
        })}
      </nav>

      {/* CSS keyframes loaded via import '../styles/dashboard-keyframes.css' */}
    </div>
  );
}
